// src/pages/pos/PosHeader.jsx
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import LanguageSwitcher from "../../InitialPage/Sidebar/LanguageSwitcher";
import { useNavigate } from "react-router-dom";
import { logout } from "../../api/authApi";
import {
  Download,
  FileText,
  BarChart2,
  Users,
  List,
  Printer,
  RotateCcw,
  Plus,
  Trash2,
} from "react-feather";
import {
  fetchSalesReport,
  fetchDailyBreakdownReport,
  fetchCashierPerformanceReport,
  fetchProductsReport,
} from "../../api/website-api/web-report";
import {
  listVendorOrders,
  getPosOrderById,
  getVendorOrderById,
} from "../../api/website-api/web-pos-order";
import {
  refundVendorPosOrder,
  exchangeVendorPosOrder,
} from "../../api/website-api/web-order";
import { fetchRefundPolicy } from "../../api/website-api/pagesApi";
import { fetchWebProducts } from "../../api/website-api/web-product";
import html2canvas from "html2canvas";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import {
  Modal,
  Button,
  Input,
  Select,
  Row,
  Col,
  Card,
  Typography,
  Space,
  Tag,
  Spin,
  Alert,
} from "antd";
import {
  SearchOutlined,
  SwapOutlined,
  DollarOutlined,
} from "@ant-design/icons";

const { Text, Paragraph } = Typography;
const { Option } = Select;

// ─────────────────────────────────────────────
// LANGUAGE HELPERS
// ─────────────────────────────────────────────
const getCurrentLang = () => {
  const lang = (localStorage.getItem("lang") || "en").toLowerCase();
  return lang.startsWith("ar") ? "ar" : "en";
};
const isArabicLang = () => getCurrentLang() === "ar";

// ─────────────────────────────────────────────
// EXCHANGE MODAL TRANSLATIONS
// ─────────────────────────────────────────────
const exchangeI18n = {
  en: {
    title: "Exchange Order",
    subtitle: "Swap returned items with confidence",
    description:
      "Choose the returned product, pick a replacement, and finish the payment details in one clean flow.",
    badge: "Exchange Flow",
    returnedItem: "Returned Item",
    returnedItemSub: "Select what the customer is bringing back.",
    originalItem: "Original Item",
    returnQty: "Return Qty",
    missingId: "Missing POS order product ID",
    qty: "Qty",
    returnId: "Return ID",
    replacementItem: "Replacement Item",
    replacementItemSub:
      "Search by product name or SKU and choose the best match.",
    searchProduct: "Search Product",
    searchPlaceholder: "Search by name or SKU…",
    search: "Search",
    loading: "Loading…",
    noProducts: "No products found.",
    selectedProduct: "Selected",
    productId: "ID",
    sku: "SKU",
    price: "Price",
    stock: "Stock",
    unitPrice: "Unit Price",
    discount: "Discount",
    subtotal: "Subtotal",
    total: "Total",
    options: "Options",
    optionsPlaceholder: '{"size":"M"}',
    paymentNotes: "Payment & Notes",
    paymentNotesSub:
      "Capture how the balance is settled and keep any internal note.",
    paymentType: "Payment Type",
    amountPaid: "Amount Paid",
    cash: "Cash",
    card: "Card",
    knet: "KNET",
    split: "Split",
    splitDetails: "Split Payment Details",
    splitTotal: "Split Total",
    addSplit: "+ Add Split",
    method: "Method",
    amount: "Amount",
    reference: "Reference",
    refPlaceholder: "Ref #",
    notes: "Notes",
    notesPlaceholder: "e.g. Size exchange",
    submitExchange: "Submit Exchange",
    cancel: "Cancel",
    validationReturnItem: "Please select or enter a returned item ID.",
    validationReturnQty: "Return quantity must be greater than 0.",
    validationNewProduct: "Please search and select a new product.",
    validationNewQty: "New item quantity must be greater than 0.",
    validationDiscount: "Discount cannot be negative.",
    validationAmountPaid: "Amount paid cannot be negative.",
    validationSplitMin: "Please keep at least two split payment rows.",
    validationSplitAmount:
      "Each split payment row must have an amount greater than 0.",
    manualIdLabel: "POS Order Product ID",
    manualIdWarning:
      "We could not detect order items from the API response, so enter the pos_order_product_id manually.",
    exchangeSuccess: "Exchange Successful",
    exchangeSuccessText: "Order exchange completed successfully.",
    exchangeFailed: "Exchange Failed",
    exchangeFailedText: "Failed to exchange this order.",
    remove: "Remove",
    exchanging: "Exchanging…",
    exchange: "Exchange",
  },
  ar: {
    title: "استبدال الطلب",
    subtitle: "استبدل العناصر المُرتجعة بثقة",
    description:
      "اختر المنتج المُرتجع، حدد البديل، وأنهِ تفاصيل الدفع في خطوة واحدة.",
    badge: "سير الاستبدال",
    returnedItem: "العنصر المُرتجع",
    returnedItemSub: "حدد ما يعيده العميل.",
    originalItem: "العنصر الأصلي",
    returnQty: "كمية الإرجاع",
    missingId: "رقم منتج الطلب غير موجود",
    qty: "الكمية",
    returnId: "رقم الإرجاع",
    replacementItem: "العنصر البديل",
    replacementItemSub: "ابحث باسم المنتج أو SKU واختر الأنسب.",
    searchProduct: "بحث عن منتج",
    searchPlaceholder: "ابحث بالاسم أو SKU…",
    search: "بحث",
    loading: "جارٍ التحميل…",
    noProducts: "لم يُعثر على منتجات.",
    selectedProduct: "المحدد",
    productId: "المعرّف",
    sku: "SKU",
    price: "السعر",
    stock: "المخزون",
    unitPrice: "سعر الوحدة",
    discount: "الخصم",
    subtotal: "الإجمالي الفرعي",
    total: "الإجمالي",
    options: "خيارات",
    optionsPlaceholder: '{"size":"M"}',
    paymentNotes: "الدفع والملاحظات",
    paymentNotesSub: "سجّل كيفية تسوية الرصيد وأضف أي ملاحظة داخلية.",
    paymentType: "طريقة الدفع",
    amountPaid: "المبلغ المدفوع",
    cash: "نقدي",
    card: "بطاقة",
    knet: "كي نت",
    split: "مُجزّأ",
    splitDetails: "تفاصيل الدفع المُجزّأ",
    splitTotal: "إجمالي التجزئة",
    addSplit: "+ إضافة تجزئة",
    method: "الطريقة",
    amount: "المبلغ",
    reference: "المرجع",
    refPlaceholder: "رقم المرجع",
    notes: "ملاحظات",
    notesPlaceholder: "مثال: استبدال مقاس",
    submitExchange: "تأكيد الاستبدال",
    cancel: "إلغاء",
    validationReturnItem: "يرجى اختيار أو إدخال معرّف العنصر المُرتجع.",
    validationReturnQty: "يجب أن تكون كمية الإرجاع أكبر من 0.",
    validationNewProduct: "يرجى البحث عن منتج جديد واختياره.",
    validationNewQty: "يجب أن تكون كمية العنصر الجديد أكبر من 0.",
    validationDiscount: "لا يمكن أن يكون الخصم سالباً.",
    validationAmountPaid: "لا يمكن أن يكون المبلغ المدفوع سالباً.",
    validationSplitMin: "يرجى الإبقاء على صفّين على الأقل للدفع المُجزّأ.",
    validationSplitAmount:
      "يجب أن يكون مبلغ كل صف في الدفع المُجزّأ أكبر من 0.",
    manualIdLabel: "رقم منتج طلب نقطة البيع",
    manualIdWarning:
      "لم نتمكن من اكتشاف عناصر الطلب من الاستجابة، لذا أدخل pos_order_product_id يدوياً.",
    exchangeSuccess: "تم الاستبدال بنجاح",
    exchangeSuccessText: "تم إتمام استبدال الطلب بنجاح.",
    exchangeFailed: "فشل الاستبدال",
    exchangeFailedText: "فشل في استبدال هذا الطلب.",
    remove: "حذف",
    exchanging: "جارٍ الاستبدال…",
    exchange: "استبدال",
  },
};

// ─────────────────────────────────────────────
// VALUE EXTRACTION HELPERS
// ─────────────────────────────────────────────
const extractValue = (value) => {
  if (value == null) return "-";
  if (typeof value === "string" || typeof value === "number") return value;
  if (typeof value === "object") {
    const lang = getCurrentLang();
    if (value[lang] != null) return value[lang];
    if (value.value != null) return value.value;
    if (value.amount != null) return value.amount;
    if (value.total != null) return value.total;
    if (value.count != null) return value.count;
    if (value.label != null) return value.label;
    if (value.name != null) return value.name;
    if (value.title != null) return value.title;
    const primitive = Object.values(value).find(
      (v) => typeof v === "string" || typeof v === "number",
    );
    if (primitive != null) return primitive;
  }
  return String(value);
};

const safeNumber = (value) => {
  const v = extractValue(value);
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(3) : v;
};

const safeText = (value) => {
  const v = extractValue(value);
  return v == null ? "-" : String(v);
};

const formatCouponsUsedList = (value) => {
  const coupons = toSafeArray(value);
  if (!coupons.length) return "-";
  return coupons
    .map((coupon) => {
      const code = safeText(coupon?.code ?? coupon?.name ?? coupon?.coupon_id);
      const timesUsed = safeText(coupon?.times_used ?? 0);
      const discountTotal = safeNumber(coupon?.discount_total ?? 0);
      return `${code} x${timesUsed} (${discountTotal})`;
    })
    .join(" | ");
};

const toSafeArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];
  const candidates = [
    value.data,
    value.results,
    value.items,
    value.list,
    value.rows,
  ];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
  }
  const values = Object.values(value);
  if (
    values.length > 0 &&
    values.every(
      (entry) => entry && typeof entry === "object" && !Array.isArray(entry),
    )
  ) {
    return values;
  }
  return [];
};

const formatYmdForReport = (value = "") => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, year, month, day] = match;
    return `${day}-${month}-${year}`;
  }
  const dmyMatch = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dmyMatch) return raw;
  return raw;
};

const toComparableYmd = (value = "") => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const ymdMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymdMatch) return `${ymdMatch[1]}-${ymdMatch[2]}-${ymdMatch[3]}`;
  const dmyMatch = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dmyMatch) return `${dmyMatch[3]}-${dmyMatch[2]}-${dmyMatch[1]}`;
  return "";
};

const getOrderedReportPeriod = (fromValue = "", toValue = "") => {
  const from = formatYmdForReport(fromValue);
  const to = formatYmdForReport(toValue);
  const comparableFrom = toComparableYmd(from);
  const comparableTo = toComparableYmd(to);
  if (comparableFrom && comparableTo && comparableFrom > comparableTo) {
    return [to, from];
  }
  return [from, to];
};

// ─────────────────────────────────────────────
// BILINGUAL REPORT LABELS
// ─────────────────────────────────────────────
const reportLabelPairs = {
  salesSummary: "Sales Summary Report / تقرير ملخص المبيعات",
  period: "Period / الفترة",
  metric: "Metric / المقياس",
  value: "Value / القيمة",
  paymentMethod: "Payment Method / طريقة الدفع",
  total: "Total / الإجمالي",
  count: "Count / العدد",
  totalSales: "Total Sales / إجمالي المبيعات",
  totalOrders: "Total Orders / إجمالي الطلبات",
  refundedOrders: "Refunded Orders / الطلبات المسترجعة",
  cancelledOrders: "Cancelled Orders / الطلبات الملغاة",
  totalItemsSold: "Total Items Sold / إجمالي العناصر المباعة",
  totalDiscount: "Total Discount / إجمالي الخصم",
  totalRefunds: "Total Refunds / إجمالي المرتجعات",
  netSales: "Net Sales / صافي المبيعات",
  avgOrderValue: "Average Order Value / متوسط قيمة الطلب",
  cash: "Cash / نقدي",
  card: "Card / بطاقة",
  knet: "KNET / كي نت",
  split: "Split / متعدد",
  dailyBreakdownReport: "Daily Breakdown Report / Daily Breakdown Report",
  topProducts: "Top Products Report / تقرير أفضل المنتجات",
  productId: "Product ID / رقم المنتج",
  productName: "Product Name / اسم المنتج",
  quantity: "Quantity / الكمية",
  revenue: "Revenue / الإيراد",
  discount: "Discount / الخصم",
  orders: "Orders / الطلبات",
  cashierReport: "Cashier Performance Report / تقرير أداء الكاشير",
  cashierId: "Cashier ID / رقم الكاشير",
  cashierName: "Cashier Name / اسم الكاشير",
  email: "Email / البريد الإلكتروني",
  role: "Role / الدور",
  avgOrder: "Avg Order / متوسط الطلب",
  items: "Items / العناصر",
  sessions: "Sessions / الجلسات",
  refunds: "Refunds / المرتجعات",
  refundTotal: "Refund Total / إجمالي المرتجعات",
  netSalesShort: "Net Sales / صافي المبيعات",
  couponsApplied: "Coupons Applied / عدد القسائم المستخدمة",
  couponDiscountTotal: "Coupon Discount Total / إجمالي خصم القسائم",
  uniqueCoupons: "Unique Coupons / القسائم الفريدة",
  couponsUsedList: "Coupons Used List / قائمة القسائم المستخدمة",
  date: "Date / التاريخ",
  cashiers: "Cashiers / عدد الكاشير",
  salesSummarySheet: "Sales Summary / ملخص المبيعات",
  cashierReportsSheet: "Cashier Reports / تقارير الكاشير",
  dailyBreakdownSheet: "Daily Breakdown / التفصيل اليومي",
  topProductsSheet: "Top Products / أفضل المنتجات",
  cashierPerformanceSheet: "Cashier Performance / أداء الكاشير",
};

const splitReportLabel = (value = "") => {
  const text = String(value || "").trim();
  if (!text) return { en: "", ar: "" };
  const parts = text.split(" / ");
  if (parts.length >= 2) {
    return {
      en: String(parts[0] || "").trim(),
      ar: String(parts.slice(1).join(" / ") || "").trim(),
    };
  }
  return { en: text, ar: text };
};

const t = new Proxy(
  {},
  {
    get: (_, key) => {
      if (typeof key !== "string") return "";
      const rawValue = reportLabelPairs[key];
      if (rawValue == null) return key;
      const { en, ar } = splitReportLabel(rawValue);
      return getCurrentLang() === "ar" ? ar || en : en || ar;
    },
  },
);

// ─────────────────────────────────────────────
// HTML ESCAPE
// ─────────────────────────────────────────────
const escapeHtml = (value) => {
  if (value == null) return "-";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

// ─────────────────────────────────────────────
// PDF HELPERS
// ─────────────────────────────────────────────
const buildTableRows = (
  rows,
  { cellPadding = "10px 12px", fontSize = "13px" } = {},
) =>
  rows
    .map(
      (row) => `
    <tr>
      ${row
        .map(
          (cell) => `
          <td style="border:1px solid #d7dee3;padding:${cellPadding};font-size:${fontSize};color:#222;vertical-align:middle;">
            ${escapeHtml(cell)}
          </td>`,
        )
        .join("")}
    </tr>`,
    )
    .join("");

const renderHtmlToPdf = async ({
  html,
  fileName = "report.pdf",
  containerWidth = 900,
  orientation = "p",
}) => {
  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.left = "-10000px";
  container.style.top = "0";
  container.style.width = `${containerWidth}px`;
  container.style.background = "#ffffff";
  container.style.padding = "24px";
  container.style.zIndex = "-1";
  container.innerHTML = html;
  document.body.appendChild(container);
  try {
    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
    });
    const imgData = canvas.toDataURL("image/png");
    const doc = new jsPDF(orientation, "pt", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const imgWidth = pageWidth - margin * 2;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = margin;
    doc.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
    while (heightLeft > 0) {
      position = heightLeft - imgHeight + margin;
      doc.addPage();
      doc.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }
    doc.save(fileName);
  } finally {
    document.body.removeChild(container);
  }
};

// ─────────────────────────────────────────────
// INVOICE HELPERS (unchanged from original)
// ─────────────────────────────────────────────
const invoiceLabels = {
  en: {
    invoice: "Invoice",
    orderId: "Order ID",
    date: "Date",
    paymentType: "Payment Type",
    cashier: "Payment Receiver",
    items: "Items",
    qty: "Qty",
    price: "Price",
    lineTotal: "Total",
    subtotal: "Subtotal",
    discount: "Discount",
    total: "Total",
    received: "Received",
    paying: "Paying",
    change: "Change",
    thankYou: "Thank you for your purchase!",
    invoiceNo: "Invoice No",
    refundPolicy: "Refund Policy",
    printSave: "Print / Save as PDF",
    back: "Back",
    printTip: "Tip: In the print dialog choose Save as PDF to download.",
    noPolicy: "Refund policy is not available.",
    splitDetails: "Split Payment Details",
    splitMethod: "Method",
    splitAmount: "Amount",
    splitReference: "Reference",
    splitNoReference: "-",
  },
  ar: {
    invoice: "فاتورة",
    orderId: "رقم الطلب",
    date: "التاريخ",
    paymentType: "طريقة الدفع",
    cashier: "المستلم",
    items: "العناصر",
    qty: "الكمية",
    price: "السعر",
    lineTotal: "الإجمالي",
    subtotal: "الإجمالي الفرعي",
    discount: "الخصم",
    total: "الإجمالي",
    received: "المبلغ المستلم",
    paying: "المبلغ المدفوع",
    change: "الباقي",
    thankYou: "شكراً لتسوقكم!",
    invoiceNo: "رقم الفاتورة",
    refundPolicy: "سياسة الاسترجاع",
    printSave: "طباعة / حفظ PDF",
    back: "رجوع",
    printTip: "ملاحظة: من نافذة الطباعة اختر حفظ كملف PDF للتنزيل.",
    noPolicy: "سياسة الاسترجاع غير متوفرة.",
    splitDetails: "تفاصيل الدفع المجزأ",
    splitMethod: "الطريقة",
    splitAmount: "المبلغ",
    splitReference: "المرجع",
    splitNoReference: "-",
  },
};

const invoiceNumber = (value = 0, lang = "en") => {
  const n = Number(value || 0);
  return (Number.isFinite(n) ? n : 0).toLocaleString(
    lang === "ar" ? "ar-SA" : "en-US",
    { minimumFractionDigits: 2, maximumFractionDigits: 2 },
  );
};

const firstPresent = (...values) => {
  for (const value of values) {
    if (value == null) continue;
    const parsed = extractValue(value);
    if (parsed == null) continue;
    if (typeof parsed === "string" && !parsed.trim()) continue;
    return parsed;
  }
  return "";
};

const toNum = (value, fallback = 0) => {
  const n = Number(extractValue(value));
  return Number.isFinite(n) ? n : fallback;
};

const normalizePolicyText = (value) => {
  if (!value) return "";
  const raw = String(value).trim();
  const withBreaks = raw
    .replace(/<\s*br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|h[1-6]|tr|table)>/gi, "\n");
  const stripped = withBreaks.replace(/<[^>]*>/g, " ");
  const decoded = stripped
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&#039;/gi, "'");
  return decoded
    .split(/\r?\n/)
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .join("\n");
};

const detectOrderLang = (order = {}) => {
  const raw = String(
    firstPresent(
      order.language_code,
      order.language,
      order.lang,
      order.languageCode,
      order.language_id,
      order.languageId,
    ) || "",
  ).toLowerCase();
  if (raw.startsWith("ar") || raw === "4" || raw.includes("arabic"))
    return "ar";
  if (raw.startsWith("en") || raw === "1" || raw.includes("english"))
    return "en";
  return getCurrentLang();
};

const resolveInvoiceLang = (order = {}) => {
  const selectedLang = getCurrentLang();
  if (selectedLang === "ar" || selectedLang === "en") return selectedLang;
  return detectOrderLang(order);
};

const getOrderId = (order = {}) =>
  String(
    firstPresent(
      order.order_id,
      order.pos_order_id,
      order.id,
      order.pk,
      order.increment_id,
      order.order_number,
    ) || "-",
  );

const getPosOrderId = (order = {}) =>
  String(
    firstPresent(
      order.pos_order_id,
      order.posOrderId,
      order.pos_order?.id,
      order.pos_order?.pos_order_id,
    ) || "",
  ).trim();

const getOrderStatusValue = (order = {}) =>
  String(
    firstPresent(
      order.order_status,
      order.status,
      order.orderStatus,
      order.data?.order_status,
      order.data?.status,
    ) || "",
  )
    .trim()
    .toLowerCase();

const isOrderRefunded = (order = {}) => {
  const status = getOrderStatusValue(order);
  return status.includes("refund");
};

const paymentTypeLabel = (value) => {
  const v = String(value || "").trim();
  const k = v.toLowerCase();
  if (k.includes("cash")) return "Cash";
  if (k.includes("card")) return "Card";
  if (k.includes("knet")) return "KNET";
  if (k.includes("split")) return "Split";
  return v || "-";
};

const extractOrderItems = (order = {}) => {
  const candidateArrays = [
    order.items,
    order.order_items,
    order.orderItems,
    order.products,
    order.order_products,
    order.lines,
    order.line_items,
    order.data?.items,
  ];
  const itemsRaw =
    candidateArrays.find((arr) => Array.isArray(arr) && arr.length > 0) || [];
  return itemsRaw.map((item) => {
    const qty = toNum(
      firstPresent(item.quantity, item.qty, item.product_quantity),
      0,
    );
    const lineTotal = toNum(
      firstPresent(item.total, item.line_total, item.subtotal),
      0,
    );
    const price = toNum(
      firstPresent(
        item.price,
        item.unit_price,
        qty > 0 ? lineTotal / qty : 0,
        item.amount,
      ),
      0,
    );
    const explicitLineDiscount = toNum(
      firstPresent(item.discount, item.discount_amount, item.product_discount),
      0,
    );
    const derivedLineDiscount = Math.max(0, price * qty - lineTotal);
    const lineDiscount =
      explicitLineDiscount > 0 ? explicitLineDiscount : derivedLineDiscount;
    const resolvedLineTotal =
      lineTotal > 0 ? lineTotal : Math.max(0, price * qty - lineDiscount);
    return {
      name: String(
        firstPresent(item.name, item.product_name, item.title, item.model) ||
          "Item",
      ),
      quantity: qty || 0,
      price: price || 0,
      lineTotal: resolvedLineTotal || 0,
      lineDiscount: lineDiscount || 0,
    };
  });
};

const extractPaymentSplits = (order = {}) => {
  const candidateArrays = [
    order.payment_splits,
    order.paymentSplits,
    order.split_payments,
    order.splitPayments,
    order.payment_details,
    order.paymentDetails,
    order.payments,
    order.order?.payment_splits,
    order.order?.paymentSplits,
    order.data?.payment_splits,
    order.data?.paymentSplits,
    order.data?.split_payments,
    order.data?.splitPayments,
    order.data?.payment_details,
    order.data?.paymentDetails,
    order.data?.payments,
    order.data?.order?.payment_splits,
    order.data?.order?.paymentSplits,
  ];
  const paymentSplitsRaw =
    candidateArrays.find((arr) => Array.isArray(arr) && arr.length > 0) || [];
  const normalized = paymentSplitsRaw
    .map((split) => ({
      method: paymentTypeLabel(
        firstPresent(
          split.payment_type,
          split.method,
          split.type,
          split.name,
          split.payment_method,
        ),
      ),
      amount: toNum(
        firstPresent(split.amount, split.paid_amount, split.value, split.total),
        0,
      ),
      reference: String(
        firstPresent(
          split.reference,
          split.ref,
          split.transaction_reference,
          split.transaction_id,
        ) || "",
      ),
    }))
    .filter(
      (split) =>
        split.method !== "-" ||
        Number(split.amount || 0) > 0 ||
        Boolean(split.reference),
    );
  if (normalized.length > 0) return normalized;
  const derived = [
    {
      method: "Cash",
      amount: toNum(
        firstPresent(
          order.cash_amount,
          order.cash_paid,
          order.cash,
          order.data?.cash_amount,
        ),
        0,
      ),
      reference: "",
    },
    {
      method: "Card",
      amount: toNum(
        firstPresent(
          order.card_amount,
          order.card_paid,
          order.card,
          order.data?.card_amount,
        ),
        0,
      ),
      reference: "",
    },
    {
      method: "KNET",
      amount: toNum(
        firstPresent(
          order.knet_amount,
          order.knet_paid,
          order.knet,
          order.data?.knet_amount,
        ),
        0,
      ),
      reference: "",
    },
  ].filter((split) => Number(split.amount || 0) > 0);
  return derived;
};

const normalizeOrderForInvoice = (order = {}) => {
  const items = extractOrderItems(order);
  const paymentSplits = extractPaymentSplits(order);
  const paymentType = paymentTypeLabel(
    firstPresent(order.payment_type, order.payment_method, order.payment_mode),
  );
  const subtotal = items.reduce(
    (sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0),
    0,
  );
  const total = toNum(
    firstPresent(
      order.total_amount,
      order.total,
      order.grand_total,
      order.final_total,
    ),
    subtotal,
  );
  const orderLevelDiscount = toNum(
    firstPresent(order.discount_amount, order.discount, order.total_discount),
    0,
  );
  const itemLevelDiscount = items.reduce(
    (sum, item) => sum + Number(item.lineDiscount || 0),
    0,
  );
  const impliedDiscount = Math.max(0, subtotal - total);
  const discountAmount = Math.max(
    impliedDiscount,
    orderLevelDiscount,
    itemLevelDiscount,
  );
  const received = toNum(
    firstPresent(order.amount_paid, order.received_amount, order.paid_amount),
    total,
  );
  const paying = toNum(
    firstPresent(order.paying_amount, order.payable_amount, order.amount_due),
    total,
  );
  const computedChange = Math.max(0, received - paying);
  const change = toNum(
    firstPresent(order.change, order.change_amount),
    computedChange,
  );
  return {
    orderId: getOrderId(order),
    orderStatus: getOrderStatusValue(order),
    isRefunded: isOrderRefunded(order),
    type: paymentType,
    invoiceNo: String(
      firstPresent(
        order.invoice_no,
        order.invoice_number,
        order.reference,
        order.transaction_reference,
      ) || "",
    ),
    receivedAmount: received,
    payingAmount: paying,
    change,
    paymentReceiver: String(
      firstPresent(
        order.payment_receiver,
        order.cashier_name,
        order.created_by_name,
        order.user_name,
      ) || "-",
    ),
    totalAmount: total,
    discountAmount,
    createdAt:
      firstPresent(order.created_at, order.date_added, order.createdAt) ||
      new Date().toISOString(),
    items,
    paymentSplits,
    isSplitPayment:
      paymentType.toLowerCase().includes("split") || paymentSplits.length > 0,
  };
};

const buildInvoiceHtml = (tx, lang = "en", refundPolicy = "") => {
  const L = invoiceLabels[lang] || invoiceLabels.en;
  const enLabels = invoiceLabels.en;
  const dir = lang === "ar" ? "rtl" : "ltr";
  const alignStart = lang === "ar" ? "right" : "left";
  const alignEnd = lang === "ar" ? "left" : "right";
  const subtotal2 = (tx.items || []).reduce(
    (sum, it) => sum + Number(it.price || 0) * Number(it.quantity || 0),
    0,
  );
  const total =
    typeof tx.totalAmount === "number"
      ? tx.totalAmount
      : Number(tx.totalAmount || subtotal2);
  const discount =
    typeof tx.discountAmount === "number"
      ? tx.discountAmount
      : Number(tx.discountAmount || Math.max(0, subtotal2 - total));
  const received = Number(tx.receivedAmount ?? 0);
  const paying = Number(tx.payingAmount ?? total);
  const changeVal = Number(tx.change ?? Math.max(0, received - paying));
  const todayStr = new Date(tx.createdAt || Date.now()).toLocaleString(
    lang === "ar" ? "ar-SA" : "en-US",
  );
  const rows = (tx.items || [])
    .map(
      (it) => `
    <tr>
      <td>${escapeHtml(it.name || "")}</td>
      <td style="text-align:center">${invoiceNumber(it.quantity || 0, lang)}</td>
      <td style="text-align:${alignEnd}">${invoiceNumber(it.price || 0, lang)}</td>
      <td style="text-align:${alignEnd}">${invoiceNumber(it.lineTotal ?? (it.price || 0) * (it.quantity || 0), lang)}</td>
    </tr>`,
    )
    .join("");
  const invoiceNoRow = tx.invoiceNo
    ? `<div><span>${L.invoiceNo}</span><strong>${escapeHtml(tx.invoiceNo)}</strong></div>`
    : "";
  const hasSplitRows =
    Array.isArray(tx.paymentSplits) && tx.paymentSplits.length > 0;
  const splitDetailsSection =
    (tx.isSplitPayment || hasSplitRows) && hasSplitRows
      ? `<div class="split-block">
          <div class="split-title">${L.splitDetails || enLabels.splitDetails}</div>
          <table class="split-table">
            <thead><tr>
              <th class="start">${L.splitMethod || enLabels.splitMethod}</th>
              <th class="end">${L.splitAmount || enLabels.splitAmount}</th>
              <th class="start">${L.splitReference || enLabels.splitReference}</th>
            </tr></thead>
            <tbody>
              ${tx.paymentSplits
                .map(
                  (split) => `<tr>
                <td>${escapeHtml(split.method || "-")}</td>
                <td class="end">${invoiceNumber(split.amount || 0, lang)}</td>
                <td>${escapeHtml(split.reference || L.splitNoReference || enLabels.splitNoReference)}</td>
              </tr>`,
                )
                .join("")}
            </tbody>
          </table>
        </div>`
      : "";
  const suggestedFileName = `Invoice-${tx.orderId || "order"}.pdf`;
  const storeWebsiteUrl = "https://nomination.kw/";
  const policyText = normalizePolicyText(refundPolicy || L.noPolicy);
  const policyHtml = escapeHtml(policyText).replace(/\n/g, "<br/>");
  const refundedStamp = tx.isRefunded
    ? `<div class="refund-stamp">${lang === "ar" ? "مُرتجَع / REFUNDED" : "REFUNDED / مُرتجَع"}</div>`
    : "";
  return `<!DOCTYPE html>
<html lang="${lang}" dir="${dir}">
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>${L.invoice} - ${escapeHtml(tx.orderId || "")}</title>
<style>
  :root{color-scheme:light}*{box-sizing:border-box}
  body{font-family:Arial,"Tahoma","Segoe UI",sans-serif;margin:0;padding:12px;background:#f6f6f6;direction:${dir};text-align:${alignStart};color:#111827}
  .wrap{position:relative;width:100%;max-width:900px;margin:0 auto;background:#fff;border:1px solid #e5e7eb;border-radius:14px;padding:18px}
  .refund-stamp{position:absolute;top:88px;${lang === "ar" ? "left:22px" : "right:22px"};border:3px solid #dc2626;color:#dc2626;font-size:22px;font-weight:900;letter-spacing:1.6px;text-transform:uppercase;padding:8px 16px;border-radius:10px;transform:rotate(-16deg);background:rgba(254,242,242,.7);pointer-events:none;z-index:2}
  h1{font-size:20px;margin:0 0 14px;text-align:center;color:#111827}
  .meta{font-size:13px;margin-bottom:16px;background:#fafafa;border:1px solid #eee;border-radius:10px;padding:10px 12px}
  .meta div{display:flex;gap:8px;justify-content:space-between;padding:5px 0;align-items:flex-start}
  .meta span{opacity:.75}.meta strong{text-align:${alignEnd};word-break:break-word}
  .split-block{margin:10px 0 12px;border:1px solid #ececec;border-radius:10px;overflow:hidden}
  .split-title{padding:8px 12px;font-size:13px;font-weight:700;background:#f8fafc;border-bottom:1px solid #ececec}
  .split-table{margin-top:0;font-size:12.5px}
  .split-table th,.split-table td{padding:8px 10px;border-bottom:1px solid #f0f0f0}
  .split-table tbody tr:last-child td{border-bottom:0}
  table{width:100%;border-collapse:collapse;font-size:13px;margin-top:6px}
  th,td{padding:10px 8px;border-bottom:1px dashed #ddd;vertical-align:top}
  th{text-align:${alignStart};font-weight:bold;background:#f8f8f8}
  tfoot td{border:0;padding:7px 8px}
  .total-row td{border-top:2px solid #000;font-weight:bold;padding-top:10px}
  .thanks{text-align:center;margin-top:16px;font-size:13px;font-weight:600}
  .policy-plain{margin-top:10px;text-align:center;font-size:13px;line-height:1.6;font-weight:600;word-break:break-word;direction:${dir}}
  .store-url{margin-top:10px;text-align:center;font-size:12px;color:#6b7280;word-break:break-word}
  .end{text-align:${alignEnd}}.start{text-align:${alignStart}}
  .actions{display:flex;gap:10px;justify-content:center;align-items:center;margin-bottom:12px;flex-wrap:wrap}
  .btn{border:0;border-radius:10px;padding:10px 14px;font-size:14px;cursor:pointer}
  .btn-primary{background:#0d6efd;color:#fff}.btn-light{background:#f0f0f0;color:#111}
  .hint{font-size:12px;opacity:.75;text-align:center;margin-top:4px;margin-bottom:12px}
  @media print{body{background:#fff;padding:0}.wrap{border:0;border-radius:0;padding:0;max-width:100%}.actions,.hint{display:none!important}.policy-plain{page-break-inside:avoid;break-inside:avoid;margin-top:10px}}
</style>
</head>
<body>
  <div class="wrap">
    ${refundedStamp}
    <div class="actions">
      <button class="btn btn-primary" onclick="doPrint()">${L.printSave}</button>
      <button class="btn btn-light" onclick="goBack()">${L.back}</button>
    </div>
    <div class="hint">${L.printTip}</div>
    <h1>${L.invoice} / ${lang === "ar" ? "Invoice" : "فاتورة"}</h1>
    <div class="meta">
      <div><span>${L.orderId}</span><strong>${escapeHtml(tx.orderId || "-")}</strong></div>
      <div><span>${L.date}</span><strong>${escapeHtml(todayStr)}</strong></div>
      <div><span>${L.paymentType}</span><strong>${escapeHtml(tx.type || "-")}</strong></div>
      ${invoiceNoRow}
      <div><span>${L.cashier}</span><strong>${escapeHtml(tx.paymentReceiver || "-")}</strong></div>
    </div>
    ${splitDetailsSection}
    <table>
      <thead><tr>
        <th class="start">${L.items}</th>
        <th style="text-align:center">${L.qty}</th>
        <th class="end">${L.price}</th>
        <th class="end">${L.lineTotal}</th>
      </tr></thead>
      <tbody>${rows || `<tr><td colspan="4" style="text-align:center">-</td></td>`}</tbody>
      <tfoot>
        <tr><td colspan="3" class="end">${L.subtotal}</td><td class="end">${invoiceNumber(subtotal2, lang)}</td></tr>
        ${discount > 0 ? `<tr><td colspan="3" class="end">${L.discount}</td><td class="end">-${invoiceNumber(discount, lang)}</td></tr>` : ""}
        <tr class="total-row"><td colspan="3" class="end">${L.total}</td><td class="end">${invoiceNumber(total, lang)}</td></tr>
        <tr><td colspan="3" class="end">${L.received}</td><td class="end">${invoiceNumber(received, lang)}</td></tr>
        <tr><td colspan="3" class="end">${L.paying}</td><td class="end">${invoiceNumber(paying, lang)}</td></tr>
        <tr><td colspan="3" class="end">${L.change}</td><td class="end">${invoiceNumber(changeVal, lang)}</td></tr>
      </tfoot>
    </table>
    <div class="thanks">${L.thankYou}</div>
    <div class="policy-plain">${L.refundPolicy}: ${policyHtml}</div>
    <div class="store-url">${storeWebsiteUrl}</div>
  </div>
  <script>
    document.title=${JSON.stringify(suggestedFileName)};
    function doPrint(){window.print();}
    function goBack(){window.location.href=window.location.origin+"/pos";}
  </script>
</body>
</html>`;
};

// ─────────────────────────────────────────────
// EXCHANGE MODAL COMPONENT (Ant Design, responsive)
// ─────────────────────────────────────────────
const ExchangeModal = ({
  show,
  onClose,
  orderItems,
  onSubmit,
  lang,
  onSearchProducts,
}) => {
  const L = exchangeI18n[lang] || exchangeI18n.en;

  // Form state
  const [returnItemId, setReturnItemId] = useState(
    orderItems.length > 0 ? String(orderItems[0]._posOrderProductId || "") : "",
  );
  const [returnQty, setReturnQty] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [newQty, setNewQty] = useState(1);
  const [newDiscount, setNewDiscount] = useState(0);
  const [newOptions, setNewOptions] = useState("");
  const [paymentType, setPaymentType] = useState("cash");
  const [amountPaid, setAmountPaid] = useState("0");
  const [notes, setNotes] = useState("");
  const [splitRows, setSplitRows] = useState([
    { payment_type: "cash", amount: "", reference: "" },
    { payment_type: "card", amount: "", reference: "" },
  ]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Reset when modal opens
  useEffect(() => {
    if (!show) return;
    setReturnItemId(
      orderItems.length > 0
        ? String(orderItems[0]._posOrderProductId || "")
        : "",
    );
    setReturnQty(1);
    setSearchQuery("");
    setProducts([]);
    setSelectedProduct(null);
    setNewQty(1);
    setNewDiscount(0);
    setNewOptions("");
    setPaymentType("cash");
    setAmountPaid("0");
    setNotes("");
    setSplitRows([
      { payment_type: "cash", amount: "", reference: "" },
      { payment_type: "card", amount: "", reference: "" },
    ]);
    setErrors({});
    setSubmitting(false);
  }, [show]);

  const splitTotal = splitRows.reduce(
    (sum, r) => sum + (parseFloat(r.amount) || 0),
    0,
  );

  const handleSearch = async () => {
    const query = searchQuery.trim();

    setProductsLoading(true);
    setProducts([]);

    try {
      const results = await onSearchProducts(query);
      console.log("Search results received:", results);
      console.log(
        "Results type:",
        Array.isArray(results) ? "array" : typeof results,
      );
      console.log("Results length:", results?.length || 0);

      // results should already be an array from handleSearchProducts
      const productsArray = Array.isArray(results) ? results : [];
      setProducts(productsArray);

      if (productsArray.length === 0) {
        console.log("No products found for query:", query);
      } else {
        console.log("First product sample:", productsArray[0]);
      }
    } catch (error) {
      console.error("Product search error:", error);
      setProducts([]);
    } finally {
      setProductsLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    if (!show) return;

    if (!searchQuery.trim()) {
      handleSearch();
      return undefined;
    }

    const timer = setTimeout(() => {
      handleSearch();
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery, show]);

  const handleSelectProduct = (product) => {
    console.log("Selected product:", product);
    setSelectedProduct(product);
    setErrors((prev) => ({ ...prev, selectedProduct: "" }));
  };

  const updateSplitRow = (index, field, value) => {
    setSplitRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addSplitRow = () => {
    setSplitRows((prev) => [
      ...prev,
      { payment_type: "cash", amount: "", reference: "" },
    ]);
  };

  const removeSplitRow = (index) => {
    setSplitRows((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    const errs = {};
    if (!returnItemId || !Number(returnItemId))
      errs.returnItemId = L.validationReturnItem;
    if (!returnQty || Number(returnQty) <= 0)
      errs.returnQty = L.validationReturnQty;
    if (!selectedProduct) {
      errs.selectedProduct = L.validationNewProduct;
    } else {
      const productId = firstPresent(
        selectedProduct.product_id,
        selectedProduct.id,
        selectedProduct._id,
      );
      if (!productId) errs.selectedProduct = L.validationNewProduct;
    }
    if (!newQty || Number(newQty) <= 0) errs.newQty = L.validationNewQty;
    if (Number(newDiscount) < 0) errs.newDiscount = L.validationDiscount;
    if (Number(amountPaid) < 0) errs.amountPaid = L.validationAmountPaid;
    if (paymentType === "split") {
      if (splitRows.length < 2) errs.split = L.validationSplitMin;
      else if (splitRows.some((r) => (parseFloat(r.amount) || 0) <= 0))
        errs.split = L.validationSplitAmount;
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setSubmitting(true);

    const productId = firstPresent(
      selectedProduct.product_id,
      selectedProduct.id,
      selectedProduct._id,
    );

    const payload = {
      return_items: [
        {
          pos_order_product_id: Number(returnItemId),
          quantity: Number(returnQty),
        },
      ],
      new_items: [
        {
          product_id: Number(productId),
          quantity: Number(newQty),
          discount: Number(newDiscount),
          options: newOptions,
        },
      ],
      payment_type: paymentType,
      amount_paid: Number(amountPaid),
      payment_splits:
        paymentType === "split"
          ? splitRows.map((r) => ({
              payment_type: r.payment_type || "cash",
              amount: Number(r.amount || 0),
              reference: r.reference || "",
            }))
          : [],
      notes,
    };

    try {
      await onSubmit(payload);
    } finally {
      setSubmitting(false);
    }
  };

  const hasItems = orderItems.length > 0;

  // Helper function to extract product details
  const getProductId = (product) => {
    return String(
      firstPresent(product.product_id, product.id, product._id) || "",
    );
  };

  const getProductName = (product) => {
    return String(
      firstPresent(
        product.name,
        product.Product_Name,
        product.meta_title,
        product.title,
      ) || "Unnamed",
    );
  };

  const getProductSku = (product) => {
    return String(firstPresent(product.sku, product.SKU, product.model) || "-");
  };

  const getProductPrice = (product) => {
    return Number(
      firstPresent(product.price, product.Price, product.regular_price) || 0,
    );
  };

  const getProductStock = (product) => {
    return Number(
      firstPresent(product.quantity, product.Qty, product.stock_quantity) || 0,
    );
  };

  const unitPrice = selectedProduct ? getProductPrice(selectedProduct) : 0;
  const selectedQty = Math.max(0, Number(newQty) || 0);
  const discountValue = Math.max(0, Number(newDiscount) || 0);
  const subtotalAmount = unitPrice * selectedQty;
  const appliedDiscount = Math.min(discountValue, subtotalAmount);
  const totalAmount = Math.max(0, subtotalAmount - appliedDiscount);

  useEffect(() => {
    if (paymentType === "split") {
      setAmountPaid(splitTotal.toFixed(3));
    } else {
      setAmountPaid(totalAmount.toFixed(3));
    }
  }, [splitRows, paymentType, splitTotal, totalAmount]);

  return (
    <Modal
      title={
        <Space>
          <SwapOutlined style={{ color: "#f97316" }} />
          <span>{L.title}</span>
          <Tag color="orange">{L.badge}</Tag>
        </Space>
      }
      open={show}
      onCancel={onClose}
      footer={null}
      width={900}
      centered
      destroyOnClose
      zIndex={1001}
      bodyStyle={{
        maxHeight: "calc(100vh - 200px)",
        overflowY: "auto",
        padding: "20px 24px",
      }}
      style={{ top: 20 }}
      maskClosable={false}
    >
      <Paragraph type="secondary" style={{ marginBottom: 24 }}>
        <SwapOutlined /> {L.subtitle} — {L.description}
      </Paragraph>

      {/* Returned Item Section */}
      <Card
        size="small"
        title={
          <Space>
            <Tag color="orange">1</Tag>
            <span>{L.returnedItem}</span>
          </Space>
        }
        style={{ marginBottom: 20, borderRadius: 12 }}
      >
        <Paragraph type="secondary" style={{ marginBottom: 16 }}>
          {L.returnedItemSub}
        </Paragraph>
        {hasItems ? (
          <Row gutter={16}>
            <Col span={16}>
              <div style={{ marginBottom: 8 }}>
                <Text strong>{L.originalItem}</Text>
              </div>
              <Select
                style={{ width: "100%" }}
                value={returnItemId}
                onChange={(value) => {
                  setReturnItemId(value);
                  setErrors((prev) => ({ ...prev, returnItemId: "" }));
                }}
                status={errors.returnItemId ? "error" : ""}
              >
                {orderItems.map((item, idx) => (
                  <Select.Option
                    key={idx}
                    value={String(item._posOrderProductId || "")}
                    disabled={!item._posOrderProductId}
                  >
                    {idx + 1}. {item._name} — {L.qty}: {item._qty} —{" "}
                    {L.returnId}:{" "}
                    {item._posOrderProductId || `(${L.missingId})`}
                  </Select.Option>
                ))}
              </Select>
              {errors.returnItemId && (
                <Text type="danger" style={{ fontSize: 12 }}>
                  {errors.returnItemId}
                </Text>
              )}
            </Col>
            <Col span={8}>
              <div style={{ marginBottom: 8 }}>
                <Text strong>{L.returnQty}</Text>
              </div>
              <Input
                type="number"
                min={1}
                value={returnQty}
                onChange={(e) => {
                  setReturnQty(e.target.value);
                  setErrors((prev) => ({ ...prev, returnQty: "" }));
                }}
                status={errors.returnQty ? "error" : ""}
              />
              {errors.returnQty && (
                <Text type="danger" style={{ fontSize: 12 }}>
                  {errors.returnQty}
                </Text>
              )}
            </Col>
          </Row>
        ) : (
          <>
            <Alert
              message={L.manualIdWarning}
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Row gutter={16}>
              <Col span={16}>
                <div style={{ marginBottom: 8 }}>
                  <Text strong>{L.manualIdLabel}</Text>
                </div>
                <Input
                  type="number"
                  value={returnItemId}
                  onChange={(e) => {
                    setReturnItemId(e.target.value);
                    setErrors((prev) => ({ ...prev, returnItemId: "" }));
                  }}
                  placeholder={L.manualIdLabel}
                  status={errors.returnItemId ? "error" : ""}
                />
                {errors.returnItemId && (
                  <Text type="danger" style={{ fontSize: 12 }}>
                    {errors.returnItemId}
                  </Text>
                )}
              </Col>
              <Col span={8}>
                <div style={{ marginBottom: 8 }}>
                  <Text strong>{L.returnQty}</Text>
                </div>
                <Input
                  type="number"
                  min={1}
                  value={returnQty}
                  onChange={(e) => {
                    setReturnQty(e.target.value);
                    setErrors((prev) => ({ ...prev, returnQty: "" }));
                  }}
                  status={errors.returnQty ? "error" : ""}
                />
                {errors.returnQty && (
                  <Text type="danger" style={{ fontSize: 12 }}>
                    {errors.returnQty}
                  </Text>
                )}
              </Col>
            </Row>
          </>
        )}
      </Card>

      {/* Replacement Item Section */}
      <Card
        size="small"
        title={
          <Space>
            <Tag color="blue">2</Tag>
            <span>{L.replacementItem}</span>
          </Space>
        }
        style={{ marginBottom: 20, borderRadius: 12 }}
      >
        <Paragraph type="secondary" style={{ marginBottom: 16 }}>
          {L.replacementItemSub}
        </Paragraph>

        {/* Search */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col flex="auto">
            <Input
              placeholder={L.searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onPressEnter={handleSearch}
              prefix={<SearchOutlined />}
              allowClear
            />
          </Col>
          <Col>
            <Button
              type="primary"
              onClick={handleSearch}
              loading={productsLoading}
              icon={<SearchOutlined />}
            >
              {L.search}
            </Button>
          </Col>
        </Row>

        {/* Selected Product */}
        {selectedProduct && (
          <Alert
            message={
              <Space>
                <span>
                  {L.selectedProduct}:{" "}
                  <strong>{getProductName(selectedProduct)}</strong>
                  (ID: {getProductId(selectedProduct)}) — {L.price}:{" "}
                  {getProductPrice(selectedProduct).toFixed(3)} — {L.stock}:{" "}
                  {getProductStock(selectedProduct)}
                </span>
              </Space>
            }
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
            closable
            onClose={() => setSelectedProduct(null)}
          />
        )}
        {errors.selectedProduct && (
          <Text
            type="danger"
            style={{ fontSize: 12, display: "block", marginBottom: 16 }}
          >
            {errors.selectedProduct}
          </Text>
        )}

        {/* Product List */}
        <div
          style={{
            border: "1px solid #d9d9d9",
            borderRadius: 8,
            maxHeight: 300,
            overflowY: "auto",
            marginBottom: 16,
          }}
        >
          {productsLoading ? (
            <div style={{ textAlign: "center", padding: 40 }}>
              <Spin tip={L.loading} />
            </div>
          ) : products.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: "#999" }}>
              {searchQuery.trim() ? L.noProducts : L.noProducts}
            </div>
          ) : (
            products.map((product, idx) => {
              const pid = getProductId(product);
              const pname = getProductName(product);
              const psku = getProductSku(product);
              const pprice = getProductPrice(product);
              const pqty = getProductStock(product);
              const isSelected =
                selectedProduct && getProductId(selectedProduct) === pid;

              return (
                <div
                  key={idx}
                  onClick={() => handleSelectProduct(product)}
                  style={{
                    padding: "12px 16px",
                    borderBottom: "1px solid #f0f0f0",
                    cursor: "pointer",
                    backgroundColor: isSelected ? "#e6f7ff" : "transparent",
                    transition: "background 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected)
                      e.currentTarget.style.backgroundColor = "#fafafa";
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected)
                      e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <Space
                    direction="vertical"
                    size={2}
                    style={{ width: "100%" }}
                  >
                    <Text strong>{pname}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {L.productId}: {pid} | {L.sku}: {psku} | {L.price}:{" "}
                      {pprice.toFixed(3)} | {L.stock}: {pqty}
                    </Text>
                  </Space>
                </div>
              );
            })
          )}
        </div>

        {/* Qty, Discount, Options */}
        <Row gutter={16}>
          <Col span={8}>
            <div style={{ marginBottom: 8 }}>
              <Text strong>{L.qty}</Text>
            </div>
            <Input
              type="number"
              min={1}
              value={newQty}
              onChange={(e) => {
                setNewQty(e.target.value);
                setErrors((prev) => ({ ...prev, newQty: "" }));
              }}
              status={errors.newQty ? "error" : ""}
            />
            {errors.newQty && (
              <Text type="danger" style={{ fontSize: 12 }}>
                {errors.newQty}
              </Text>
            )}
          </Col>
          <Col span={8}>
            <div style={{ marginBottom: 8 }}>
              <Text strong>{L.discount}</Text>
            </div>
            <Input
              type="number"
              min={0}
              step={0.001}
              value={newDiscount}
              onChange={(e) => {
                setNewDiscount(e.target.value);
                setErrors((prev) => ({ ...prev, newDiscount: "" }));
              }}
              status={errors.newDiscount ? "error" : ""}
            />
            {errors.newDiscount && (
              <Text type="danger" style={{ fontSize: 12 }}>
                {errors.newDiscount}
              </Text>
            )}
          </Col>
          <Col span={8}>
            <div style={{ marginBottom: 8 }}>
              <Text strong>{L.options}</Text>
            </div>
            <Input
              placeholder={L.optionsPlaceholder}
              value={newOptions}
              onChange={(e) => setNewOptions(e.target.value)}
            />
          </Col>
        </Row>

        {selectedProduct && (
          <Card
            size="small"
            style={{
              marginTop: 16,
              borderRadius: 10,
              background: "#fff7ed",
              border: "1px solid #fed7aa",
            }}
          >
            <Row gutter={16}>
              <Col span={6}>
                <Text type="secondary">{L.unitPrice}</Text>
                <div>
                  <Text strong>{unitPrice.toFixed(3)}</Text>
                </div>
              </Col>
              <Col span={6}>
                <Text type="secondary">{L.subtotal}</Text>
                <div>
                  <Text strong>{subtotalAmount.toFixed(3)}</Text>
                </div>
              </Col>
              <Col span={6}>
                <Text type="secondary">{L.discount}</Text>
                <div>
                  <Text strong>{appliedDiscount.toFixed(3)}</Text>
                </div>
              </Col>
              <Col span={6}>
                <Text type="secondary">{L.total}</Text>
                <div>
                  <Text strong style={{ color: "#ea580c" }}>
                    {totalAmount.toFixed(3)}
                  </Text>
                </div>
              </Col>
            </Row>
          </Card>
        )}
      </Card>

      {/* Payment & Notes Section */}
      <Card
        size="small"
        title={
          <Space>
            <Tag color="purple">3</Tag>
            <span>{L.paymentNotes}</span>
          </Space>
        }
        style={{ marginBottom: 20, borderRadius: 12 }}
      >
        <Paragraph type="secondary" style={{ marginBottom: 16 }}>
          {L.paymentNotesSub}
        </Paragraph>

        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={12}>
            <div style={{ marginBottom: 8 }}>
              <Text strong>{L.paymentType}</Text>
            </div>
            <Select
              style={{ width: "100%" }}
              value={paymentType}
              onChange={setPaymentType}
            >
              <Option value="cash">{L.cash}</Option>
              <Option value="card">{L.card}</Option>
              <Option value="knet">{L.knet}</Option>
              <Option value="split">{L.split}</Option>
            </Select>
          </Col>
          <Col span={12}>
            <div style={{ marginBottom: 8 }}>
              <Text strong>{L.amountPaid}</Text>
            </div>
            <Input
              type="number"
              min={0}
              step={0.001}
              value={amountPaid}
              disabled={paymentType === "split"}
              style={
                paymentType === "split" ? { backgroundColor: "#f5f5f5" } : {}
              }
              onChange={(e) => {
                if (paymentType !== "split") {
                  setAmountPaid(e.target.value);
                  setErrors((prev) => ({ ...prev, amountPaid: "" }));
                }
              }}
              status={errors.amountPaid ? "error" : ""}
            />
            {errors.amountPaid && (
              <Text type="danger" style={{ fontSize: 12 }}>
                {errors.amountPaid}
              </Text>
            )}
          </Col>
        </Row>

        {paymentType === "split" && (
          <div
            style={{
              border: "1px solid #d9d9d9",
              borderRadius: 8,
              padding: 16,
              marginBottom: 16,
              backgroundColor: "#fafafa",
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 12 }}>
              <DollarOutlined /> {L.splitDetails}
            </div>

            {/* Headers */}
            <Row gutter={16} style={{ marginBottom: 8 }}>
              <Col span={7}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {L.method}
                </Text>
              </Col>
              <Col span={7}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {L.amount}
                </Text>
              </Col>
              <Col span={8}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {L.reference}
                </Text>
              </Col>
              <Col span={2} />
            </Row>

            {splitRows.map((row, idx) => (
              <Row
                key={idx}
                gutter={16}
                style={{ marginBottom: 8 }}
                align="middle"
              >
                <Col span={7}>
                  <Select
                    style={{ width: "100%" }}
                    value={row.payment_type}
                    onChange={(val) => updateSplitRow(idx, "payment_type", val)}
                  >
                    <Option value="cash">{L.cash}</Option>
                    <Option value="card">{L.card}</Option>
                    <Option value="knet">{L.knet}</Option>
                  </Select>
                </Col>
                <Col span={7}>
                  <Input
                    type="number"
                    min={0}
                    step={0.001}
                    placeholder={L.amount}
                    value={row.amount}
                    onChange={(e) =>
                      updateSplitRow(idx, "amount", e.target.value)
                    }
                  />
                </Col>
                <Col span={8}>
                  <Input
                    placeholder={L.refPlaceholder}
                    value={row.reference}
                    onChange={(e) =>
                      updateSplitRow(idx, "reference", e.target.value)
                    }
                  />
                </Col>
                <Col span={2}>
                  <Button
                    type="text"
                    danger
                    icon={<Trash2 size={16} />}
                    disabled={splitRows.length <= 2}
                    onClick={() => removeSplitRow(idx)}
                  />
                </Col>
              </Row>
            ))}

            {errors.split && (
              <Text
                type="danger"
                style={{ fontSize: 12, display: "block", marginBottom: 8 }}
              >
                {errors.split}
              </Text>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: 8,
              }}
            >
              <Button
                type="dashed"
                onClick={addSplitRow}
                icon={<Plus size={14} />}
              >
                {L.addSplit}
              </Button>
              <Tag color="green">
                {L.splitTotal}: {splitTotal.toFixed(3)} / {totalAmount.toFixed(3)}
              </Tag>
            </div>
          </div>
        )}

        <div>
          <div style={{ marginBottom: 8 }}>
            <Text strong>{L.notes}</Text>
          </div>
          <Input.TextArea
            rows={3}
            placeholder={L.notesPlaceholder}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </Card>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 12,
          marginTop: 8,
        }}
      >
        <Button onClick={onClose} disabled={submitting}>
          {L.cancel}
        </Button>
        <Button
          type="primary"
          onClick={handleSubmit}
          loading={submitting}
          icon={<SwapOutlined />}
          style={{ background: "#f97316", borderColor: "#f97316" }}
        >
          {submitting ? L.exchanging : L.submitExchange}
        </Button>
      </div>
    </Modal>
  );
};

ExchangeModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  orderItems: PropTypes.arrayOf(
    PropTypes.shape({
      _posOrderProductId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      _name: PropTypes.string,
      _qty: PropTypes.number,
    }),
  ).isRequired,
  onSubmit: PropTypes.func.isRequired,
  lang: PropTypes.oneOf(["en", "ar"]).isRequired,
  onSearchProducts: PropTypes.func.isRequired,
};

// ─────────────────────────────────────────────
// MAIN PosHeader COMPONENT
// ─────────────────────────────────────────────
const PosHeader = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [reportStartDate, setReportStartDate] = useState("");
  const [reportEndDate, setReportEndDate] = useState("");
  const [reportDateError, setReportDateError] = useState("");

  // Order History
  const [showOrderHistoryModal, setShowOrderHistoryModal] = useState(false);
  const [orderHistory, setOrderHistory] = useState([]);
  const [orderHistoryPage, setOrderHistoryPage] = useState(1);
  const [orderHistoryTotal, setOrderHistoryTotal] = useState(0);
  const [orderHistoryHasNext, setOrderHistoryHasNext] = useState(false);
  const [orderHistoryLoading, setOrderHistoryLoading] = useState(false);
  const [orderHistoryError, setOrderHistoryError] = useState("");
  const [orderHistoryStartDate, setOrderHistoryStartDate] = useState("");
  const [orderHistoryEndDate, setOrderHistoryEndDate] = useState("");
  const [orderHistoryPaymentType, setOrderHistoryPaymentType] = useState("");
  const [orderHistoryDateError, setOrderHistoryDateError] = useState("");
  const [orderHistoryFilteredCache, setOrderHistoryFilteredCache] = useState(
    [],
  );
  const [orderHistoryFilterCacheKey, setOrderHistoryFilterCacheKey] =
    useState("");
  const [orderHistoryFilterNextPage, setOrderHistoryFilterNextPage] =
    useState(1);
  const [orderHistoryFilterExhausted, setOrderHistoryFilterExhausted] =
    useState(false);
  const [printingOrderId, setPrintingOrderId] = useState("");
  const [refundingOrderId, setRefundingOrderId] = useState("");

  // Exchange Modal State
  const [showExchangeModal, setShowExchangeModal] = useState(false);
  const [exchangeModalOrder, setExchangeModalOrder] = useState(null);
  const [exchangeModalItems, setExchangeModalItems] = useState([]);
  const [exchangingOrderId, setExchangingOrderId] = useState("");

  const orderHistoryPageLimit = 10;

  const navigate = useNavigate();

  const currentLang = (localStorage.getItem("lang") || "en").toLowerCase();
  const isArabic = currentLang.startsWith("ar");
  const lang = isArabic ? "ar" : "en";

  // Add styles for proper z-index
  useEffect(() => {
    // Add global styles to ensure proper z-index layering
    const style = document.createElement("style");
    style.innerHTML = `
      .pos-history-modal-backdrop {
        z-index: 1000 !important;
      }
      .pos-history-modal-card {
        z-index: 1001 !important;
      }
      .ant-modal-root {
        z-index: 1050 !important;
      }
      .ant-modal-mask {
        z-index: 1049 !important;
      }
      .ant-modal-wrap {
        z-index: 1050 !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const formattedTime = currentTime.toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  // ── Export helpers ──────────────────────────────────────────────
  const exportToExcel = (reportData, fileName) => {
    const workbook = XLSX.utils.book_new();
    const summary = reportData?.summary || {};
    const payment = summary?.payment_breakdown || {};
    const summaryRow = {
      [t.totalSales]: safeNumber(summary.total_sales),
      [t.totalOrders]: safeText(summary.total_orders),
      [t.refundedOrders]: safeText(summary?.refunded_orders?.count ?? 0),
      [t.cancelledOrders]: safeText(summary?.cancelled_orders?.count ?? 0),
      [t.totalItemsSold]: safeText(summary.total_items_sold),
      [t.totalDiscount]: safeNumber(summary.total_discount),
      [t.totalRefunds]: safeNumber(summary.total_refunds),
      [t.netSales]: safeNumber(summary.net_sales),
      [t.avgOrderValue]: safeNumber(summary.average_order_value),
      [`${t.cash} ${t.total}`]: safeNumber(payment?.cash?.total),
      [`${t.cash} ${t.count}`]: safeText(payment?.cash?.count),
      [`${t.card} ${t.total}`]: safeNumber(payment?.card?.total),
      [`${t.card} ${t.count}`]: safeText(payment?.card?.count),
      [`${t.knet} ${t.total}`]: safeNumber(payment?.knet?.total),
      [`${t.knet} ${t.count}`]: safeText(payment?.knet?.count),
      [`${t.split} ${t.total}`]: safeNumber(payment?.split?.total),
      [`${t.split} ${t.count}`]: safeText(payment?.split?.count),
    };
    const summarySheet = XLSX.utils.json_to_sheet([summaryRow]);
    XLSX.utils.book_append_sheet(workbook, summarySheet, t.salesSummarySheet);
    const cashierRows = toSafeArray(reportData?.cashier_reports).map((c) => ({
      [t.cashierId]: safeText(c.cashier_id),
      [t.cashierName]: safeText(c.cashier_name),
      [t.totalOrders]: safeText(c.total_orders),
      [t.totalSales]: safeNumber(c.total_sales),
      [t.totalItemsSold]: safeText(c.total_items_sold),
      [t.totalDiscount]: safeNumber(c.total_discount),
      [t.netSales]: safeNumber(c.net_sales),
      [t.avgOrderValue]: safeNumber(c.average_order_value),
      [`${t.cash} ${t.total}`]: safeNumber(c?.payment_breakdown?.cash?.total),
      [`${t.cash} ${t.count}`]: safeText(c?.payment_breakdown?.cash?.count),
      [`${t.knet} ${t.total}`]: safeNumber(c?.payment_breakdown?.knet?.total),
      [`${t.knet} ${t.count}`]: safeText(c?.payment_breakdown?.knet?.count),
    }));
    const cashierSheet = XLSX.utils.json_to_sheet(cashierRows);
    XLSX.utils.book_append_sheet(workbook, cashierSheet, t.cashierReportsSheet);
    const dailyRows = toSafeArray(reportData?.daily_breakdown).map((d) => ({
      [t.date]: safeText(d.date),
      [t.totalOrders]: safeText(d.total_orders),
      [t.totalSales]: safeNumber(d.total_sales),
      [t.items]: safeText(d.total_items),
      [t.cashiers]: safeText(d.cashier_count),
    }));
    const dailySheet = XLSX.utils.json_to_sheet(dailyRows);
    XLSX.utils.book_append_sheet(workbook, dailySheet, t.dailyBreakdownSheet);
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  const exportSalesSummaryPDF = async (
    reportData,
    { selectedStartDate = "", selectedEndDate = "" } = {},
  ) => {
    const summary = reportData?.summary || {};
    const period = reportData?.period || {};
    const payment = summary?.payment_breakdown || {};
    const isAr = isArabicLang();
    const [periodFrom, periodTo] = getOrderedReportPeriod(
      selectedStartDate || safeText(period.from),
      selectedEndDate || safeText(period.to),
    );
    const html = `<div style="font-family:Tahoma,Arial,'Segoe UI',sans-serif;color:#111;background:#fff;direction:${isAr ? "rtl" : "ltr"};">
      <h1 style="font-size:28px;margin:0 0 18px;font-weight:700;">${escapeHtml(t.salesSummary)}</h1>
      <div style="font-size:18px;margin-bottom:24px;"><strong>${escapeHtml(t.period)}:</strong> ${escapeHtml(periodFrom)} - ${escapeHtml(periodTo)}</div>
      <table style="width:100%;border-collapse:collapse;margin-bottom:24px;">
        <thead><tr style="background:#4d84bd;color:white;">
          <th style="padding:12px;text-align:${isAr ? "right" : "left"};">${escapeHtml(t.metric)}</th>
          <th style="padding:12px;text-align:${isAr ? "right" : "left"};">${escapeHtml(t.value)}</th>
        </tr></thead>
        <tbody>${buildTableRows([
          [t.totalSales, safeNumber(summary.total_sales)],
          [t.totalOrders, safeText(summary.total_orders)],
          [t.refundedOrders, safeText(summary?.refunded_orders?.count ?? 0)],
          [t.cancelledOrders, safeText(summary?.cancelled_orders?.count ?? 0)],
          [t.totalItemsSold, safeText(summary.total_items_sold)],
          [t.totalDiscount, safeNumber(summary.total_discount)],
          [t.totalRefunds, safeNumber(summary.total_refunds)],
          [t.netSales, safeNumber(summary.net_sales)],
          [t.avgOrderValue, safeNumber(summary.average_order_value)],
        ])}</tbody>
      </table>
      <table style="width:100%;border-collapse:collapse;">
        <thead><tr style="background:#4d84bd;color:white;">
          <th style="padding:12px;text-align:${isAr ? "right" : "left"};">${escapeHtml(t.paymentMethod)}</th>
          <th style="padding:12px;text-align:${isAr ? "right" : "left"};">${escapeHtml(t.total)}</th>
          <th style="padding:12px;text-align:${isAr ? "right" : "left"};">${escapeHtml(t.count)}</th>
        </tr></thead>
        <tbody>${buildTableRows([
          [
            t.cash,
            safeNumber(payment?.cash?.total),
            safeText(payment?.cash?.count),
          ],
          [
            t.card,
            safeNumber(payment?.card?.total),
            safeText(payment?.card?.count),
          ],
          [
            t.knet,
            safeNumber(payment?.knet?.total),
            safeText(payment?.knet?.count),
          ],
          [
            t.split,
            safeNumber(payment?.split?.total),
            safeText(payment?.split?.count),
          ],
        ])}</tbody>
      </table>
    </div>`;
    await renderHtmlToPdf({
      html,
      fileName: isAr ? "sales-report-ar.pdf" : "sales-report-en.pdf",
    });
  };

  const exportProductsToExcel = (products, fileName) => {
    const workbook = XLSX.utils.book_new();
    const formattedRows = (products || []).map((p) => ({
      [t.productId]: safeText(p.product_id),
      [t.productName]: safeText(p.product_name),
      [t.quantity]: safeText(p.total_quantity),
      [t.revenue]: safeNumber(p.total_revenue),
      [t.discount]: safeNumber(p.total_discount),
      [t.orders]: safeText(p.order_count),
    }));
    const sheet = XLSX.utils.json_to_sheet(formattedRows);
    sheet["!cols"] = [
      { wch: 18 },
      { wch: 40 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
    ];
    XLSX.utils.book_append_sheet(workbook, sheet, t.topProductsSheet);
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  const exportProductsToPDF = async (products, title) => {
    const isAr = isArabicLang();
    const rows = (products || []).map((p) => [
      safeText(p.product_id),
      safeText(p.product_name),
      safeText(p.total_quantity),
      safeNumber(p.total_revenue),
      safeNumber(p.total_discount),
      safeText(p.order_count),
    ]);
    const html = `<div style="font-family:Tahoma,Arial,'Segoe UI',sans-serif;color:#111;background:#fff;direction:${isAr ? "rtl" : "ltr"};">
      <h1 style="font-size:26px;margin:0 0 24px;font-weight:700;">${escapeHtml(t.topProducts)}</h1>
      <table style="width:100%;border-collapse:collapse;">
        <thead><tr style="background:#4d84bd;color:#fff;">
          ${[t.productId, t.productName, t.quantity, t.revenue, t.discount, t.orders].map((h) => `<th style="padding:12px;text-align:${isAr ? "right" : "left"};">${escapeHtml(h)}</th>`).join("")}
        </tr></thead>
        <tbody>${buildTableRows(rows)}</tbody>
      </table>
    </div>`;
    await renderHtmlToPdf({ html, fileName: `${title}.pdf` });
  };

  const exportDailyBreakdownToExcel = (dailyBreakdown, fileName) => {
    const workbook = XLSX.utils.book_new();
    const rows = (dailyBreakdown || []).map((d) => ({
      [t.date]: formatYmdForReport(safeText(d.date)),
      [t.totalOrders]: safeText(d.total_orders),
      [t.totalSales]: safeNumber(d.total_sales),
      [t.items]: safeText(d.total_items),
      [t.cashiers]: safeText(d.cashier_count),
    }));
    const sheet = XLSX.utils.json_to_sheet(rows);
    sheet["!cols"] = [
      { wch: 16 },
      { wch: 16 },
      { wch: 18 },
      { wch: 16 },
      { wch: 16 },
    ];
    XLSX.utils.book_append_sheet(workbook, sheet, t.dailyBreakdownSheet);
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  const exportDailyBreakdownToPDF = async (
    dailyBreakdown,
    { selectedStartDate = "", selectedEndDate = "", period = {} } = {},
  ) => {
    const isAr = isArabicLang();
    const [periodFrom, periodTo] = getOrderedReportPeriod(
      selectedStartDate || safeText(period.from),
      selectedEndDate || safeText(period.to),
    );
    const rows = (dailyBreakdown || []).map((d) => [
      formatYmdForReport(safeText(d.date)),
      safeText(d.total_orders),
      safeNumber(d.total_sales),
      safeText(d.total_items),
      safeText(d.cashier_count),
    ]);
    const html = `<div style="font-family:Tahoma,Arial,'Segoe UI',sans-serif;color:#111;background:#fff;direction:${isAr ? "rtl" : "ltr"};">
      <h1 style="font-size:28px;margin:0 0 18px;font-weight:700;">${escapeHtml(t.dailyBreakdownReport)}</h1>
      <div style="font-size:18px;margin-bottom:24px;"><strong>${escapeHtml(t.period)}:</strong> ${escapeHtml(periodFrom)} - ${escapeHtml(periodTo)}</div>
      <table style="width:100%;border-collapse:collapse;">
        <thead><tr style="background:#4d84bd;color:#fff;">
          ${[t.date, t.totalOrders, t.totalSales, t.items, t.cashiers].map((h) => `<th style="padding:12px;text-align:${isAr ? "right" : "left"};">${escapeHtml(h)}</th>`).join("")}
        </tr></thead>
        <tbody>${buildTableRows(rows)}</tbody>
      </table>
    </div>`;
    await renderHtmlToPdf({
      html,
      fileName: `${getLocalizedReportFileName("daily-breakdown-report")}.pdf`,
    });
  };

  const exportCashierToExcel = (cashiers, fileName) => {
    const workbook = XLSX.utils.book_new();
    const formattedRows = (cashiers || []).map((c) => ({
      [t.cashierId]: safeText(c.cashier_id),
      [t.cashierName]: safeText(c.cashier_name),
      [t.email]: safeText(c.email),
      [t.role]: safeText(c.role),
      [t.totalOrders]: safeText(c.total_orders),
      [t.totalSales]: safeNumber(c.total_sales),
      [t.avgOrder]: safeNumber(c.average_order_value),
      [t.totalItemsSold]: safeText(c.total_items_sold),
      [t.sessions]: safeText(c.total_sessions),
      [t.refunds]: safeText(c.refund_count),
      [t.refundTotal]: safeNumber(c.refund_total),
      [t.netSalesShort]: safeNumber(c.net_sales),
      [t.couponsApplied]: safeText(c.coupons_applied ?? 0),
      [t.couponDiscountTotal]: safeNumber(c.coupon_discount_total ?? 0),
      [t.uniqueCoupons]: safeText(c.unique_coupons ?? 0),
      [t.couponsUsedList]: formatCouponsUsedList(c.coupons_used_list),
    }));
    const sheet = XLSX.utils.json_to_sheet(formattedRows);
    sheet["!cols"] = [
      { wch: 16 },
      { wch: 28 },
      { wch: 30 },
      { wch: 20 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
      { wch: 16 },
      { wch: 16 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
      { wch: 22 },
      { wch: 16 },
      { wch: 52 },
    ];
    XLSX.utils.book_append_sheet(workbook, sheet, t.cashierPerformanceSheet);
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  const exportCashierToPDF = async (cashiers, title) => {
    const isAr = isArabicLang();
    const rows = (cashiers || []).map((c) => [
      safeText(c.cashier_id),
      safeText(c.cashier_name),
      safeText(c.email),
      safeText(c.role),
      safeText(c.total_orders),
      safeNumber(c.total_sales),
      safeNumber(c.average_order_value),
      safeText(c.total_items_sold),
      safeText(c.total_sessions),
      safeText(c.refund_count),
      safeNumber(c.refund_total),
      safeNumber(c.net_sales),
      safeText(c.coupons_applied ?? 0),
      safeNumber(c.coupon_discount_total ?? 0),
      safeText(c.unique_coupons ?? 0),
      formatCouponsUsedList(c.coupons_used_list),
    ]);
    const headers = [
      t.cashierId,
      t.cashierName,
      t.email,
      t.role,
      t.orders,
      t.totalSales,
      t.avgOrder,
      t.items,
      t.sessions,
      t.refunds,
      t.refundTotal,
      t.netSalesShort,
      t.couponsApplied,
      t.couponDiscountTotal,
      t.uniqueCoupons,
      t.couponsUsedList,
    ];
    const html = `<div style="font-family:Tahoma,Arial,'Segoe UI',sans-serif;color:#111;background:#fff;direction:${isAr ? "rtl" : "ltr"};">
      <h1 style="font-size:24px;margin:0 0 24px;font-weight:700;">${escapeHtml(t.cashierReport)}</h1>
      <table style="width:100%;border-collapse:collapse;">
        <thead><tr style="background:#4d84bd;color:#fff;">${headers.map((h) => `<th style="padding:10px;text-align:${isAr ? "right" : "left"};">${escapeHtml(h)}</th>`).join("")}</tr></thead>
        <tbody>${buildTableRows(rows, { cellPadding: "6px 8px", fontSize: "11px" })}</tbody>
      </table>
    </div>`;
    await renderHtmlToPdf({
      html,
      fileName: `${title}.pdf`,
      containerWidth: 1700,
      orientation: "l",
    });
  };

  const getLocalizedReportFileName = (baseName) =>
    `${baseName}-${getCurrentLang() === "ar" ? "ar" : "en"}`;

  const getReportDateError = (startDateValue = "", endDateValue = "") => {
    if (!startDateValue || !endDateValue) return "";
    if (startDateValue <= endDateValue) return "";
    return (
      window.translate?.("From date cannot be after To date.") ||
      "From date cannot be after To date."
    );
  };

  const handleClearReportDateFilter = () => {
    setReportStartDate("");
    setReportEndDate("");
    setReportDateError("");
  };

  const handleDownloadReport = async (type, format) => {
    const dateError = getReportDateError(reportStartDate, reportEndDate);
    if (dateError) {
      setReportDateError(dateError);
      return;
    }
    try {
      setReportDateError("");
      let response;
      if (type === "dailyBreakdown") {
        response = await fetchDailyBreakdownReport({
          start_date: reportStartDate || undefined,
          end_date: reportEndDate || undefined,
        });
        const reportData = response?.data || response;
        const dailyBreakdown = toSafeArray(reportData?.daily_breakdown);
        if (!dailyBreakdown.length) {
          alert(window.translate("No daily breakdown report data found!"));
          return;
        }
        if (format === "excel") {
          exportDailyBreakdownToExcel(
            dailyBreakdown,
            getLocalizedReportFileName("daily-breakdown-report"),
          );
          return;
        }
        if (format === "pdf") {
          await exportDailyBreakdownToPDF(dailyBreakdown, {
            selectedStartDate: reportStartDate,
            selectedEndDate: reportEndDate,
            period: reportData?.period || {},
          });
          return;
        }
      }
      if (type === "sales") {
        response = await fetchSalesReport({
          start_date: reportStartDate || undefined,
          end_date: reportEndDate || undefined,
        });
        const reportData = response?.data || response;
        if (!reportData) {
          alert("No sales report data found!");
          return;
        }
        if (format === "excel") {
          exportToExcel(reportData, getLocalizedReportFileName("sales-report"));
          return;
        }
        if (format === "pdf") {
          await exportSalesSummaryPDF(reportData, {
            selectedStartDate: reportStartDate,
            selectedEndDate: reportEndDate,
          });
          return;
        }
      }
      if (type === "products") {
        response = await fetchProductsReport({
          start_date: reportStartDate || undefined,
          end_date: reportEndDate || undefined,
        });
        const productsData = toSafeArray(response?.data || response);
        if (!productsData.length) {
          alert("No product report data found!");
          return;
        }
        if (format === "excel") {
          exportProductsToExcel(
            productsData,
            getLocalizedReportFileName("products-report"),
          );
          return;
        }
        if (format === "pdf") {
          await exportProductsToPDF(
            productsData,
            getLocalizedReportFileName("products-report"),
          );
          return;
        }
      }
      if (type === "cashier") {
        response = await fetchCashierPerformanceReport({
          start_date: reportStartDate || undefined,
          end_date: reportEndDate || undefined,
        });
        const cashierList = toSafeArray(response?.data || response);
        if (!cashierList.length) {
          alert("No cashier report data found!");
          return;
        }
        if (format === "excel") {
          exportCashierToExcel(
            cashierList,
            getLocalizedReportFileName("cashier-report"),
          );
          return;
        }
        if (format === "pdf") {
          await exportCashierToPDF(
            cashierList,
            getLocalizedReportFileName("cashier-report"),
          );
          return;
        }
      }
    } catch (error) {
      console.error("Report Download Error:", error);
      alert("Failed to download report!");
    }
  };

  // ── Order History helpers ────────────────────────────────────────
  const getOrderHistoryDateError = (startDateValue = "", endDateValue = "") => {
    if (!startDateValue || !endDateValue) return "";
    if (startDateValue <= endDateValue) return "";
    return (
      window.translate?.("From date cannot be after To date.") ||
      "From date cannot be after To date."
    );
  };

  const toLocalYmd = (value) => {
    if (!value) return "";
    const parsedDate = new Date(value);
    if (!Number.isNaN(parsedDate.getTime())) {
      const year = parsedDate.getFullYear();
      const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
      const day = String(parsedDate.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
    const raw = String(value).trim();
    const isoMatch = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    return isoMatch ? isoMatch[1] : "";
  };

  const getOrderDateValue = (order = {}) =>
    firstPresent(
      order.created_at,
      order.date_added,
      order.createdAt,
      order.date,
    );

  const isOrderWithinDateRange = (
    order,
    startDateValue = "",
    endDateValue = "",
  ) => {
    const orderDate = toLocalYmd(getOrderDateValue(order));
    if (!orderDate) return false;
    if (startDateValue && orderDate < startDateValue) return false;
    if (endDateValue && orderDate > endDateValue) return false;
    return true;
  };

  const normalizePaymentTypeValue = (value = "") => {
    const raw = String(value || "")
      .trim()
      .toLowerCase();
    if (!raw) return "";
    if (raw.includes("split")) return "split";
    if (raw.includes("knet")) return "knet";
    if (raw.includes("card")) return "card";
    if (raw.includes("cash")) return "cash";
    return raw;
  };

  const getOrderPaymentTypeValue = (order = {}) =>
    normalizePaymentTypeValue(
      firstPresent(
        order.payment_type,
        order.payment_method,
        order.payment_mode,
      ),
    );

  const isOrderMatchingPaymentType = (order, paymentTypeValue = "") => {
    const normalized = normalizePaymentTypeValue(paymentTypeValue);
    if (!normalized) return true;
    return getOrderPaymentTypeValue(order) === normalized;
  };

  const parseNextPage = (nextValue, currentPageValue) => {
    if (!nextValue) return Math.max(1, Number(currentPageValue || 1)) + 1;
    if (typeof nextValue === "number" && Number.isFinite(nextValue))
      return Math.max(1, Math.floor(nextValue));
    if (typeof nextValue === "string") {
      const regexMatch = nextValue.match(/[?&]page=(\d+)/i);
      if (regexMatch?.[1]) {
        const parsed = Number(regexMatch[1]);
        if (Number.isFinite(parsed) && parsed > 0) return Math.floor(parsed);
      }
      try {
        const parsedUrl = new URL(nextValue, window.location.origin);
        const pageParam = Number(parsedUrl.searchParams.get("page"));
        if (Number.isFinite(pageParam) && pageParam > 0)
          return Math.floor(pageParam);
      } catch (_e) {
        /* ignore malformed URL */
      }
    }
    return Math.max(1, Number(currentPageValue || 1)) + 1;
  };

  const resetOrderHistoryFilterCache = () => {
    setOrderHistoryFilteredCache([]);
    setOrderHistoryFilterCacheKey("");
    setOrderHistoryFilterNextPage(1);
    setOrderHistoryFilterExhausted(false);
  };

  const paginateOrderHistory = (
    allOrders = [],
    page = 1,
    { hasMoreUnfetched = false } = {},
  ) => {
    const safePage = Math.max(1, Number(page) || 1);
    const startIndex = (safePage - 1) * orderHistoryPageLimit;
    const endIndex = startIndex + orderHistoryPageLimit;
    const pageOrders = allOrders.slice(startIndex, endIndex);
    setOrderHistory(pageOrders);
    setOrderHistoryTotal(allOrders.length);
    setOrderHistoryHasNext(endIndex < allOrders.length || hasMoreUnfetched);
    setOrderHistoryPage(safePage);
  };

  const loadOrderHistory = async (
    page = 1,
    {
      startDateValue = orderHistoryStartDate,
      endDateValue = orderHistoryEndDate,
      paymentTypeValue = orderHistoryPaymentType,
      forceRefresh = false,
    } = {},
  ) => {
    const dateError = getOrderHistoryDateError(startDateValue, endDateValue);
    if (dateError) {
      setOrderHistoryDateError(dateError);
      return;
    }
    try {
      setOrderHistoryLoading(true);
      setOrderHistoryError("");
      setOrderHistoryDateError("");
      const normalizedPaymentType = normalizePaymentTypeValue(paymentTypeValue);
      const hasFilter = Boolean(
        startDateValue || endDateValue || normalizedPaymentType,
      );
      if (hasFilter) {
        const cacheKey = `${startDateValue}|${endDateValue}|${normalizedPaymentType}`;
        const isSameFilter =
          !forceRefresh &&
          cacheKey === orderHistoryFilterCacheKey &&
          Array.isArray(orderHistoryFilteredCache);
        let filteredResults = isSameFilter
          ? [...orderHistoryFilteredCache]
          : [];
        let nextPageToFetch = isSameFilter
          ? Math.max(1, Number(orderHistoryFilterNextPage || 1))
          : 1;
        let exhausted = isSameFilter
          ? Boolean(orderHistoryFilterExhausted)
          : false;
        const requiredCount =
          Math.max(1, Number(page) || 1) * orderHistoryPageLimit;
        let guard = 0;
        while (
          filteredResults.length < requiredCount &&
          !exhausted &&
          guard < 50
        ) {
          const response = await listVendorOrders({
            page: nextPageToFetch,
            pagelimit: orderHistoryPageLimit,
            date_from: startDateValue || undefined,
            date_to: endDateValue || undefined,
            payment_type: normalizedPaymentType || undefined,
          });
          const ordersBatch = Array.isArray(response?.orders)
            ? response.orders
            : [];
          filteredResults = [
            ...filteredResults,
            ...ordersBatch.filter(
              (order) =>
                isOrderWithinDateRange(order, startDateValue, endDateValue) &&
                isOrderMatchingPaymentType(order, normalizedPaymentType),
            ),
          ];
          const hasNextFromServer =
            Boolean(response?.next) && ordersBatch.length > 0;
          const batchDates = ordersBatch
            .map((order) => toLocalYmd(getOrderDateValue(order)))
            .filter(Boolean);
          const olderThanStart =
            Boolean(startDateValue) &&
            batchDates.length > 0 &&
            batchDates.every((dateValue) => dateValue < startDateValue);
          exhausted = !hasNextFromServer || olderThanStart;
          nextPageToFetch = hasNextFromServer
            ? parseNextPage(response?.next, nextPageToFetch)
            : nextPageToFetch + 1;
          guard += 1;
        }
        setOrderHistoryFilteredCache(filteredResults);
        setOrderHistoryFilterCacheKey(cacheKey);
        setOrderHistoryFilterNextPage(nextPageToFetch);
        setOrderHistoryFilterExhausted(exhausted);
        paginateOrderHistory(filteredResults, page, {
          hasMoreUnfetched: !exhausted,
        });
      } else {
        resetOrderHistoryFilterCache();
        const response = await listVendorOrders({
          page,
          pagelimit: orderHistoryPageLimit,
          payment_type: normalizedPaymentType || undefined,
        });
        setOrderHistory(Array.isArray(response?.orders) ? response.orders : []);
        setOrderHistoryTotal(Number(response?.count || 0));
        setOrderHistoryHasNext(Boolean(response?.next));
        setOrderHistoryPage(page);
      }
    } catch (error) {
      console.error("Order history fetch failed:", error);
      setOrderHistoryError(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to load order history.",
      );
      setOrderHistory([]);
      setOrderHistoryTotal(0);
      setOrderHistoryHasNext(false);
    } finally {
      setOrderHistoryLoading(false);
    }
  };

  const openOrderHistoryModal = () => {
    const resetFilters = {
      startDateValue: "",
      endDateValue: "",
      paymentTypeValue: "",
    };
    setOrderHistoryStartDate("");
    setOrderHistoryEndDate("");
    setOrderHistoryPaymentType("");
    setOrderHistoryDateError("");
    resetOrderHistoryFilterCache();
    setShowOrderHistoryModal(true);
    loadOrderHistory(1, resetFilters);
  };

  const closeOrderHistoryModal = () => setShowOrderHistoryModal(false);

  const handleApplyOrderHistoryDateFilter = () =>
    loadOrderHistory(1, {
      startDateValue: orderHistoryStartDate,
      endDateValue: orderHistoryEndDate,
      paymentTypeValue: orderHistoryPaymentType,
      forceRefresh: true,
    });

  const handleResetOrderHistoryDateFilter = () => {
    const resetFilters = {
      startDateValue: "",
      endDateValue: "",
      paymentTypeValue: "",
    };
    setOrderHistoryStartDate("");
    setOrderHistoryEndDate("");
    setOrderHistoryPaymentType("");
    setOrderHistoryDateError("");
    resetOrderHistoryFilterCache();
    loadOrderHistory(1, { ...resetFilters, forceRefresh: true });
  };

  const formatOrderDate = (order = {}) => {
    const raw = firstPresent(
      order.created_at,
      order.date_added,
      order.createdAt,
    );
    if (!raw) return "-";
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return String(raw);
    return date.toLocaleString(getCurrentLang() === "ar" ? "ar-SA" : "en-US");
  };

  const formatOrderTotal = (order = {}) => {
    const total = toNum(
      firstPresent(
        order.total_amount,
        order.total,
        order.grand_total,
        order.final_total,
      ),
      0,
    );
    return invoiceNumber(total, getCurrentLang());
  };

  const hasNextPage =
    orderHistoryHasNext ||
    orderHistoryPage * orderHistoryPageLimit < Number(orderHistoryTotal || 0);
  const hasOrderHistoryFilter = Boolean(
    orderHistoryStartDate || orderHistoryEndDate || orderHistoryPaymentType,
  );

  const fireRefundAlert = (config = {}) =>
    Swal.fire({
      ...config,
      didOpen: (popup) => {
        const container = Swal.getContainer();
        if (container) container.style.zIndex = "30000";
        if (typeof config.didOpen === "function") config.didOpen(popup);
      },
    });

  const handleRefundOrder = async (order) => {
    const posOrderId = getPosOrderId(order);
    if (!posOrderId) {
      void fireRefundAlert({
        icon: "error",
        title: window.translate("Refund Failed"),
        text: window.translate("POS order ID is missing for this order."),
        confirmButtonColor: "#dc2626",
      });
      return;
    }
    try {
      setRefundingOrderId(posOrderId);
      await refundVendorPosOrder(posOrderId);
      setOrderHistory((prev) =>
        prev.map((item) =>
          getPosOrderId(item) !== posOrderId
            ? item
            : { ...item, order_status: "refunded" },
        ),
      );
      setRefundingOrderId("");
      void fireRefundAlert({
        icon: "success",
        title: window.translate("Refund Successful"),
        text: window.translate("Product is refunded successfully."),
        confirmButtonColor: "#16a34a",
      });
      loadOrderHistory(orderHistoryPage, {
        startDateValue: orderHistoryStartDate,
        endDateValue: orderHistoryEndDate,
        paymentTypeValue: orderHistoryPaymentType,
        forceRefresh: true,
      });
    } catch (error) {
      setRefundingOrderId("");
      void fireRefundAlert({
        icon: "error",
        title: window.translate("Refund Failed"),
        text:
          error?.response?.data?.message ||
          error?.message ||
          window.translate("Failed to refund this order."),
        confirmButtonColor: "#dc2626",
      });
    }
  };

  // ── Exchange helpers ──────────────────────────────────────────────
  const getPosOrderProductId = (item = {}) =>
    String(
      firstPresent(
        item.pos_order_product_id,
        item.posOrderProductId,
        item.pos_order_product?.id,
        item.posOrderProduct?.id,
        item.order_product_id,
        item.orderProductId,
        item.pos_order_item_id,
        item.posOrderItemId,
        item.order_item_id,
        item.orderItemId,
      ) || "",
    ).trim();

  const getExchangeOrderItems = (order = {}) => {
    const candidateArrays = [
      order.items,
      order.order_items,
      order.orderItems,
      order.products,
      order.order_products,
      order.lines,
      order.line_items,
      order.data?.items,
      order.data?.order_items,
      order.data?.order_products,
    ];
    return (
      candidateArrays.find((arr) => Array.isArray(arr) && arr.length > 0) || []
    );
  };

  const handleOpenExchangeModal = async (order) => {
    const posOrderId = getPosOrderId(order);
    if (!posOrderId) {
      void fireRefundAlert({
        icon: "error",
        title: window.translate("Exchange Failed"),
        text: window.translate("POS order ID is missing for this order."),
        confirmButtonColor: "#dc2626",
      });
      return;
    }

    let orderToExchange = order;
    try {
      const detailRes = await getPosOrderById(posOrderId);
      const detailOrder =
        detailRes?.data?.data?.order ||
        detailRes?.data?.data ||
        detailRes?.data?.order ||
        detailRes?.order ||
        detailRes?.data ||
        detailRes ||
        {};
      orderToExchange = {
        ...order,
        ...(typeof detailOrder === "object" ? detailOrder : {}),
      };
    } catch (detailError) {
      console.warn(
        "Order detail fetch failed, using list payload:",
        detailError,
      );
    }

    const rawItems = getExchangeOrderItems(orderToExchange);
    const normalizedItems = rawItems.map((item) => ({
      _posOrderProductId: getPosOrderProductId(item),
      _name: String(
        firstPresent(item.name, item.product_name, item.title, item.model) ||
          "Item",
      ),
      _qty: Math.max(
        1,
        Number(
          firstPresent(item.quantity, item.qty, item.product_quantity) || 1,
        ),
      ),
      ...item,
    }));

    setExchangeModalOrder(orderToExchange);
    setExchangeModalItems(normalizedItems);
    setShowExchangeModal(true);
    setExchangingOrderId(posOrderId);
  };

  const handleExchangeSubmit = async (payload) => {
    const posOrderId = getPosOrderId(exchangeModalOrder);
    try {
      await exchangeVendorPosOrder(posOrderId, payload);
      setShowExchangeModal(false);
      setExchangingOrderId("");
      void fireRefundAlert({
        icon: "success",
        title: window.translate("Exchange Successful"),
        text: window.translate("Order exchange completed successfully."),
        confirmButtonColor: "#16a34a",
      });
      loadOrderHistory(orderHistoryPage, {
        startDateValue: orderHistoryStartDate,
        endDateValue: orderHistoryEndDate,
        paymentTypeValue: orderHistoryPaymentType,
        forceRefresh: true,
      });
    } catch (error) {
      void fireRefundAlert({
        icon: "error",
        title: window.translate("Exchange Failed"),
        text:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          window.translate("Failed to exchange this order."),
        confirmButtonColor: "#dc2626",
      });
      throw error;
    }
  };

  const handleCloseExchangeModal = () => {
    setShowExchangeModal(false);
    setExchangeModalOrder(null);
    setExchangeModalItems([]);
    setExchangingOrderId("");
  };

  const handleSearchProducts = async (query = "") => {
    try {
      const response = await fetchWebProducts({
        page: 1,
        pagelimit: 50,
        search: query,
        status: 1,
      });

      console.log("API Response for products:", response);

      // The fetchWebProducts returns an object with products array
      // So we need to return response.products, not response directly
      if (response && response.products && Array.isArray(response.products)) {
        console.log("Products found:", response.products.length);
        return response.products;
      }

      // Fallback: if response is directly an array
      if (Array.isArray(response)) {
        return response;
      }

      console.log("No products found in response");
      return [];
    } catch (error) {
      console.error("Error fetching products:", error);
      return [];
    }
  };

  // ── Invoice print ─────────────────────────────────────────────────
  const handlePrintOrderInvoice = async (order) => {
    const orderId = getOrderId(order);
    const invoiceLang = resolveInvoiceLang(order);
    try {
      setPrintingOrderId(orderId);
      let orderToPrint = order;
      if (orderId && orderId !== "-") {
        try {
          const detailRes = await getVendorOrderById(orderId);
          const detailOrder =
            detailRes?.data?.data?.order ||
            detailRes?.data?.data ||
            detailRes?.data?.order ||
            detailRes?.order ||
            detailRes?.data ||
            detailRes ||
            {};
          const detailPaymentSplits = extractPaymentSplits(detailOrder);
          const basePaymentSplits = extractPaymentSplits(order);
          orderToPrint = {
            ...order,
            ...(typeof detailOrder === "object" ? detailOrder : {}),
            payment_splits:
              detailPaymentSplits.length > 0
                ? detailPaymentSplits
                : basePaymentSplits,
            paymentSplits:
              detailPaymentSplits.length > 0
                ? detailPaymentSplits
                : basePaymentSplits,
            payment_type:
              firstPresent(
                detailOrder?.payment_type,
                detailOrder?.payment_method,
                order?.payment_type,
                order?.payment_method,
              ) ||
              (detailPaymentSplits.length > 0 || basePaymentSplits.length > 0
                ? "split"
                : "cash"),
          };
        } catch (detailError) {
          console.warn("Order detail fetch failed:", detailError);
        }
      }
      let refundPolicy = "";
      try {
        const policyRes = await fetchRefundPolicy(invoiceLang);
        refundPolicy =
          policyRes?.data?.description?.description ||
          policyRes?.description?.description ||
          policyRes?.data?.information_description?.description ||
          policyRes?.information_description?.description ||
          policyRes?.data?.content ||
          policyRes?.content ||
          "";
      } catch (policyError) {
        console.error("Failed to fetch refund policy:", policyError);
      }
      const tx = normalizeOrderForInvoice(orderToPrint);
      const html = buildInvoiceHtml(tx, invoiceLang, refundPolicy);
      const w = window.open("", "_self");
      if (!w) {
        alert("Unable to open invoice.");
        return;
      }
      w.document.open();
      w.document.write(html);
      w.document.close();
    } catch (error) {
      console.error("Failed to print historical invoice:", error);
      alert(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to print invoice.",
      );
    } finally {
      setPrintingOrderId("");
    }
  };

  const handleLogout = (e) => {
    e.preventDefault();
    logout();
    navigate("/pos/signin", { replace: true });
  };

  const toggleFullscreen = (elem) => {
    elem = elem || document.documentElement;
    if (
      !document.fullscreenElement &&
      !document.mozFullScreenElement &&
      !document.webkitFullscreenElement &&
      !document.msFullscreenElement
    ) {
      if (elem.requestFullscreen) elem.requestFullscreen();
      else if (elem.msRequestFullscreen) elem.msRequestFullscreen();
      else if (elem.mozRequestFullScreen) elem.mozRequestFullScreen();
      else if (elem.webkitRequestFullscreen)
        elem.webkitRequestFullscreen(Element.ALLOW_KEYBOARD_INPUT);
    } else {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.msExitFullscreen) document.msExitFullscreen();
      else if (document.mozCancelFullScreen) document.mozCancelFullScreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
    }
  };

  const translatedPosUser = window.translate?.("POS User");
  const posUserLabel =
    translatedPosUser && translatedPosUser !== "POS User"
      ? translatedPosUser
      : isArabic
        ? "مستخدم نقطة البيع"
        : "POS User";

  // ─────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────
  return (
    <>
      <header className="pos-header-pro">
        {/* LEFT */}
        <div className="pos-header-left">
          <ImageWithBasePath
            src="assets/img/logo.png"
            alt="Logo"
            className="pos-logo"
          />
          <div className="pos-brand">
            <h4>Hamilton POS</h4>
            <span>Point of Sale System</span>
          </div>
        </div>

        {/* RIGHT */}
        <div className="pos-header-right">
          <div className="time-badge small">
            <i className="ti ti-clock" />
            <span>{formattedTime}</span>
          </div>

          <button
            className="icon-btn primary"
            data-bs-toggle="modal"
            data-bs-target="#calculator"
          >
            <i className="ti ti-calculator" />
          </button>

          <div className="icon-btn">
            <LanguageSwitcher />
          </div>

          <button className="icon-btn" onClick={() => toggleFullscreen()}>
            <i className="ti ti-maximize" />
          </button>

          {/* Reports dropdown */}
          <div className="dropdown">
            <button
              className="report-btn dropdown-toggle"
              data-bs-toggle="dropdown"
              data-bs-auto-close="outside"
            >
              <BarChart2 size={16} />
              <span className="d-none d-md-inline">
                {window.translate("Reports")}
              </span>
            </button>
            <div
              className="dropdown-menu dropdown-menu-end report-dropdown"
              dir={isArabic ? "rtl" : "ltr"}
            >
              <div className="report-dropdown-head">
                <h6 className="report-dropdown-title">
                  <span className="report-head-icon">
                    <Download size={16} />
                  </span>
                  {window.translate("Download Reports")}
                </h6>
                <span className="report-lang-chip">
                  {currentLang === "ar" ? "AR" : "EN"}
                </span>
              </div>
              <div className="date-inputs report-filter-panel">
                <div className="report-date-field">
                  <label htmlFor="report-start-date">
                    {window.translate("From Date")}
                  </label>
                  <input
                    id="report-start-date"
                    type="date"
                    value={reportStartDate}
                    max={reportEndDate || undefined}
                    onChange={(e) => {
                      setReportStartDate(e.target.value);
                      setReportDateError("");
                    }}
                  />
                </div>
                <div className="report-date-field">
                  <label htmlFor="report-end-date">
                    {window.translate("To Date")}
                  </label>
                  <input
                    id="report-end-date"
                    type="date"
                    value={reportEndDate}
                    min={reportStartDate || undefined}
                    onChange={(e) => {
                      setReportEndDate(e.target.value);
                      setReportDateError("");
                    }}
                  />
                </div>
                <div className="report-date-actions">
                  <button
                    type="button"
                    className="mini pdf report-clear-btn"
                    onClick={handleClearReportDateFilter}
                    disabled={!reportStartDate && !reportEndDate}
                  >
                    {window.translate("Clear")}
                  </button>
                </div>
              </div>
              {reportDateError ? (
                <div className="report-date-error">{reportDateError}</div>
              ) : null}
              <div className="report-download-list">
                {[
                  {
                    label: window.translate("Sales Summary"),
                    type: "sales",
                    icon: <FileText size={15} />,
                  },
                  {
                    label: window.translate("Top Products"),
                    type: "products",
                    icon: <BarChart2 size={15} />,
                  },
                  {
                    label: window.translate("Cashier Report"),
                    type: "cashier",
                    icon: <Users size={15} />,
                  },
                  {
                    label: window.translate("Daily Breakdown Report"),
                    type: "dailyBreakdown",
                    icon: <BarChart2 size={15} />,
                  },
                ].map((item) => (
                  <div key={item.type} className="report-row report-row-pro">
                    <span className="report-row-icon">{item.icon}</span>
                    <span className="report-row-title">{item.label}</span>
                    <div className="report-row-actions">
                      <button
                        className="mini pdf report-action-btn"
                        onClick={() => handleDownloadReport(item.type, "pdf")}
                      >
                        {window.translate("PDF")}
                      </button>
                      <button
                        className="mini excel report-action-btn"
                        onClick={() => handleDownloadReport(item.type, "excel")}
                      >
                        {window.translate("Excel")}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <button className="report-btn" onClick={openOrderHistoryModal}>
            <List size={16} />
            <span className="d-none d-md-inline">
              {window.translate("Order History")}
            </span>
          </button>

          {/* User dropdown */}
          <div className="dropdown">
            <img
              src="assets/img/profiles/user-img.png"
              alt="User"
              className="user-avatar dropdown-toggle"
              data-bs-toggle="dropdown"
            />
            <div
              className="dropdown-menu dropdown-menu-end user-dropdown"
              style={{
                direction: isArabic ? "rtl" : "ltr",
                textAlign: isArabic ? "right" : "left",
              }}
            >
              <h6
                className={`fw-bold mb-2 ${isArabic ? "text-start mb-2 p-3" : "text-end mb-2 p-3"}`}
              >
                {posUserLabel}
              </h6>
              <hr />
              <button
                className={`dropdown-item text-danger ${isArabic ? "text-start mb-2 p-3" : "text-end mb-2 p-3"}`}
                style={{ textAlign: isArabic ? "right" : "left" }}
                onClick={handleLogout}
              >
                {window.translate("Logout")}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ── ORDER HISTORY MODAL ── */}
      {showOrderHistoryModal && (
        <div
          className="pos-history-modal-backdrop"
          onClick={closeOrderHistoryModal}
        >
          <div
            className="pos-history-modal-card"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            dir={isArabic ? "rtl" : "ltr"}
          >
            <div className="pos-history-modal-head">
              <h5>{window.translate("Order History")}</h5>
              <button
                type="button"
                className="pos-history-close"
                onClick={closeOrderHistoryModal}
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>

            <div className="pos-history-modal-body">
              <div className="pos-history-filters">
                <div className="pos-history-filter-field">
                  <label htmlFor="order-history-start">
                    {window.translate("From Date")}
                  </label>
                  <input
                    id="order-history-start"
                    type="date"
                    value={orderHistoryStartDate}
                    max={orderHistoryEndDate || undefined}
                    onChange={(e) => {
                      setOrderHistoryStartDate(e.target.value);
                      setOrderHistoryDateError("");
                    }}
                  />
                </div>
                <div className="pos-history-filter-field">
                  <label htmlFor="order-history-end">
                    {window.translate("To Date")}
                  </label>
                  <input
                    id="order-history-end"
                    type="date"
                    value={orderHistoryEndDate}
                    min={orderHistoryStartDate || undefined}
                    onChange={(e) => {
                      setOrderHistoryEndDate(e.target.value);
                      setOrderHistoryDateError("");
                    }}
                  />
                </div>
                <div className="pos-history-filter-field">
                  <label htmlFor="order-history-payment-type">
                    {window.translate("Payment Type")}
                  </label>
                  <select
                    id="order-history-payment-type"
                    value={orderHistoryPaymentType}
                    onChange={(e) => setOrderHistoryPaymentType(e.target.value)}
                  >
                    <option value="">{window.translate("All")}</option>
                    <option value="cash">{window.translate("Cash")}</option>
                    <option value="card">{window.translate("Card")}</option>
                    <option value="knet">{window.translate("KNET")}</option>
                    <option value="split">{window.translate("Split")}</option>
                  </select>
                </div>
                <div className="pos-history-filter-actions">
                  <button
                    type="button"
                    className="mini excel"
                    disabled={orderHistoryLoading}
                    onClick={handleApplyOrderHistoryDateFilter}
                  >
                    {window.translate("Apply")}
                  </button>
                  <button
                    type="button"
                    className="mini pdf"
                    disabled={orderHistoryLoading || !hasOrderHistoryFilter}
                    onClick={handleResetOrderHistoryDateFilter}
                  >
                    {window.translate("Clear")}
                  </button>
                </div>
              </div>

              {orderHistoryDateError ? (
                <div className="pos-history-filter-error">
                  {orderHistoryDateError}
                </div>
              ) : null}

              {orderHistoryLoading ? (
                <div className="pos-history-empty">
                  {window.translate("Loading")}...
                </div>
              ) : orderHistoryError ? (
                <div className="pos-history-error">{orderHistoryError}</div>
              ) : !orderHistory.length ? (
                <div className="pos-history-empty">
                  {window.translate("No past transactions found.")}
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm pos-history-table">
                    <thead>
                      <tr>
                        <th>{window.translate("Order ID")}</th>
                        <th>{window.translate("Date")}</th>
                        <th>{window.translate("Payment Type")}</th>
                        <th>{window.translate("Language")}</th>
                        <th className="text-end">
                          {window.translate("Total")}
                        </th>
                        <th className="text-end">
                          {window.translate("Action")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderHistory.map((order, index) => {
                        const id = getOrderId(order);
                        const posOrderId = getPosOrderId(order);
                        const orderLang = resolveInvoiceLang(order);
                        const isPrinting = printingOrderId === id;
                        const isRefunding = refundingOrderId === posOrderId;
                        const isExchanging =
                          exchangingOrderId === posOrderId && showExchangeModal;
                        const isAlreadyRefunded = isOrderRefunded(order);

                        return (
                          <tr key={`${id}-${index}`}>
                            <td>{id}</td>
                            <td>{formatOrderDate(order)}</td>
                            <td>
                              {paymentTypeLabel(
                                firstPresent(
                                  order.payment_type,
                                  order.payment_method,
                                  order.payment_mode,
                                ),
                              )}
                            </td>
                            <td>
                              <span className="pos-lang-chip">
                                {orderLang === "ar" ? "AR" : "EN"}
                              </span>
                            </td>
                            <td className="text-end">
                              {formatOrderTotal(order)}
                            </td>
                            <td className="text-end">
                              <div className="pos-history-action-group">
                                <button
                                  type="button"
                                  className="pos-history-refund-btn"
                                  onClick={() => handleRefundOrder(order)}
                                  title={window.translate("Refund Order")}
                                  disabled={
                                    !posOrderId ||
                                    isRefunding ||
                                    isAlreadyRefunded
                                  }
                                >
                                  <RotateCcw size={14} />
                                  <span>
                                    {isRefunding
                                      ? window.translate("Refunding...")
                                      : isAlreadyRefunded
                                        ? window.translate("Refunded")
                                        : window.translate("Refund")}
                                  </span>
                                </button>

                                <button
                                  type="button"
                                  className="pos-history-exchange-btn"
                                  onClick={() => handleOpenExchangeModal(order)}
                                  title={window.translate("Exchange Order")}
                                  disabled={
                                    !posOrderId ||
                                    isRefunding ||
                                    isExchanging ||
                                    isAlreadyRefunded
                                  }
                                >
                                  <RotateCcw size={14} />
                                  <span>
                                    {isExchanging
                                      ? isArabic
                                        ? "جارٍ الاستبدال…"
                                        : "Exchanging..."
                                      : isArabic
                                        ? "استبدال"
                                        : "Exchange"}
                                  </span>
                                </button>

                                <button
                                  type="button"
                                  className="pos-history-print-btn"
                                  onClick={() => handlePrintOrderInvoice(order)}
                                  title={window.translate("Print Invoice")}
                                  disabled={isPrinting}
                                >
                                  <Printer size={14} />
                                  <span>
                                    {isPrinting
                                      ? window.translate("Printing...")
                                      : window.translate("Print")}
                                  </span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="pos-history-modal-foot">
              <button
                type="button"
                className="mini excel"
                disabled={orderHistoryLoading || orderHistoryPage <= 1}
                onClick={() => loadOrderHistory(orderHistoryPage - 1)}
              >
                {window.translate("Previous")}
              </button>
              <span className="pos-history-page">
                {window.translate("Page")} {orderHistoryPage}
              </span>
              <button
                type="button"
                className="mini pdf"
                disabled={orderHistoryLoading || !hasNextPage}
                onClick={() => loadOrderHistory(orderHistoryPage + 1)}
              >
                {window.translate("Next")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── EXCHANGE MODAL (Ant Design, responsive) ── */}
      <ExchangeModal
        show={showExchangeModal}
        onClose={handleCloseExchangeModal}
        orderItems={exchangeModalItems}
        onSubmit={handleExchangeSubmit}
        lang={lang}
        onSearchProducts={handleSearchProducts}
      />
    </>
  );
};

export default PosHeader;
